import React, {useEffect, useLayoutEffect, useState, useRef, useMemo} from "react";
import {Box, GlobalStyles, TextField, Popover, IconButton, Button, Autocomplete, Typography, Divider, List, ListItemButton, ListItemText, InputAdornment, Paper, CircularProgress} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SceneIcon from "../../../librettoEditor/assets/icons/SceneIcon";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
    LexicalComposer,
    InitialConfigType,
} from "@lexical/react/LexicalComposer";
import {RichTextPlugin} from "@lexical/react/LexicalRichTextPlugin";
import {ContentEditable} from "@lexical/react/LexicalContentEditable";
import {HistoryPlugin} from "@lexical/react/LexicalHistoryPlugin";
import {OnChangePlugin} from "@lexical/react/LexicalOnChangePlugin";
import {$getRoot, $createParagraphNode, $createTextNode, $getNodeByKey, $getSelection, $isRangeSelection, $createRangeSelection, $setSelection, EditorState, LexicalEditor, KEY_DOWN_COMMAND, COMMAND_PRIORITY_HIGH, COMMAND_PRIORITY_LOW, PASTE_COMMAND, TextNode, SELECTION_CHANGE_COMMAND} from "lexical";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import {V2TranscriptWord} from "./index";
import Toolbar from "./Toolbar";
import {WordNode, $isWordNode} from "./WordNode";
import {SpeakerNode, $createSpeakerNode, $isSpeakerNode} from "./SpeakerNode";
import { MarkerNode, $createMarkerNode, $isMarkerNode } from "./MarkerNode";
import { SceneBreakNode, $createSceneBreakNode } from "./SceneBreakNode";
import {CompositionStateStore} from "../../../librettoEditor/store/CompositionStateStore";
import {CompositionService} from "../../../librettoEditor/services/CompositionService";
import {useAssetStore} from "../../../storage/AssetStore";
import {ProjectAssetsStore} from "../../../state/ProjectAssetsStore";
import {NodeEventPlugin} from "@lexical/react/LexicalNodeEventPlugin";
	import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { ContextMenuStore } from "../../../librettoEditor/store/ContextMenuStore";
	import { wordsToSentences, sentencesToParagraphs } from "./utils";
	import { getWordMidClipTimeSec, getWordStartClipTimeSec } from "./wordTime";
	import { ScriptLayerClip, isScriptClip } from "../../../engine/clips";
	import { ScriptLayerStore, DEFAULT_SPEAKER_COLORS, type SpeakerWaveformColorSegment, type SpeakerMuteTarget } from "../../../librettoEditor/store/ScriptLayerStore";
	import { observer } from "mobx-react-lite";
	import { FillerWordStore } from "../FillerWords";
	import { SpeakerOverride } from "../../../engine/clips/script/script.interfaces";
import { doc, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "../../../Firebase";
import { kTranscriptsCollectionName } from "../../../utils/CollectionConstants";
import { type TranscriptExportOptions, type TranscriptExporter } from "../transcriptExport";

// Extended word type with source tracking
interface TrackedWord extends V2TranscriptWord {
    sourceIndex: number;
    kind?: string;
    speakerId?: string | number;  // ElevenLabs returns strings like "speaker_0"
    sourceName: string;
    sourceColor: string;
    sourceGain?: number;
    energyRatio?: number;
    // Original source times (for cutting)
    srcStartSec?: number;
    srcEndSec?: number;
}

const BACKCHANNEL_WORDS = new Set([
    "yeah",
    "yep",
    "uh-huh",
    "uhhuh",
    "mhm",
    "mm-hm",
    "mmhm",
    "right",
    "okay",
    "ok",
    "sure",
    "uh",
    "um",
    "hmm",
    "hm",
    "mm",
    "aha",
]);

// Defensive transcript timing sanitization for multi-source scripts:
// some providers emit word end times that include long trailing silence (especially at turn boundaries),
// which can cause overlap suppression to hide large regions of the other speaker.
const DEFAULT_WORD_DURATION_SEC = 0.35;
const MAX_INFERRED_WORD_DURATION_SEC = 1.2;
const MAX_WORD_DURATION_SEC = 2.5;

const normalizeWordTextForHeuristics = (text: string): string =>
    String(text ?? "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const isBackchannelWord = (w: { text?: string; startSec?: number; endSec?: number; energyRatio?: number }): boolean => {
    const norm = normalizeWordTextForHeuristics(String(w.text ?? ""));
    if (!norm) return false;
    if (!BACKCHANNEL_WORDS.has(norm)) return false;

    const dur = Math.max(0, Number(w.endSec ?? 0) - Number(w.startSec ?? 0));
    if (dur > 0.6) return false;

    const energy = Number(w.energyRatio ?? NaN);
    if (Number.isFinite(energy) && energy >= 0.5) return false;
    return true;
};

const scoreForOverlapResolution = (w: {
    text?: string;
    startSec?: number;
    endSec?: number;
    energyRatio?: number;
    sourceGain?: number;
}): number => {
    const dur = Math.max(0, Number(w.endSec ?? 0) - Number(w.startSec ?? 0));
    const energy = (() => {
        const v = Number(w.energyRatio ?? NaN);
        if (!Number.isFinite(v) || v <= 0) return 0.65; // neutral fallback
        return Math.max(0, Math.min(2, v));
    })();
    const gain = (() => {
        const g = Number(w.sourceGain ?? NaN);
        if (!Number.isFinite(g) || g <= 0) return 1;
        return Math.min(4, g);
    })();

    let score = energy * gain;
    score *= 1 + Math.min(0.5, dur) * 0.25;
    if (isBackchannelWord(w)) score *= 0.25;
    return score;
};

const areLikelyDuplicateWords = (
    a: { text?: string; startSec?: number; endSec?: number },
    b: { text?: string; startSec?: number; endSec?: number },
): boolean => {
    const ta = normalizeWordTextForHeuristics(String(a.text ?? ""));
    const tb = normalizeWordTextForHeuristics(String(b.text ?? ""));
    if (!ta || !tb || ta !== tb) return false;
    const EPS_T = 0.12;
    const a0 = Number(a.startSec ?? 0);
    const b0 = Number(b.startSec ?? 0);
    const a1 = Number(a.endSec ?? a0);
    const b1 = Number(b.endSec ?? b0);
    return Math.abs(a0 - b0) <= EPS_T && Math.abs(a1 - b1) <= EPS_T;
};

function resolveOverlappingTranscriptWords<
    T extends {
        startSec?: number;
        endSec?: number;
        sourceIndex?: number;
        text?: string;
        energyRatio?: number;
        sourceGain?: number;
    },
>(words: T[], opts?: { epsSec?: number; replaceMargin?: number }): T[] {
    const epsSec = Number(opts?.epsSec ?? 0.005);
    const replaceMargin = Number(opts?.replaceMargin ?? 0.18);

    const out: T[] = [];
    for (const w of words) {
        let candidate: T | null = w;
        while (candidate && out.length > 0) {
            const prev = out[out.length - 1];
            const prevEnd = Number(prev.endSec ?? prev.startSec ?? 0);
            const candStart = Number(candidate.startSec ?? 0);
            if (candStart >= prevEnd - epsSec) break;

            const prevSrc = Number(prev.sourceIndex ?? NaN);
            const candSrc = Number(candidate.sourceIndex ?? NaN);

            // Only collapse overlaps across different sources.
            // Same-source overlaps can happen due to timestamp jitter; keep both words in that case.
            if (Number.isFinite(prevSrc) && Number.isFinite(candSrc) && prevSrc === candSrc) {
                break;
            }

            const prevScore = scoreForOverlapResolution(prev);
            const candScore = scoreForOverlapResolution(candidate);

            // Dedupe mic-bleed duplicates.
            if (areLikelyDuplicateWords(prev, candidate)) {
                if (candScore > prevScore) {
                    out.pop();
                    continue;
                }
                candidate = null;
                break;
            }

            // Keep the earlier word unless the candidate is meaningfully stronger.
            const required = prevScore * (1 + replaceMargin + (isBackchannelWord(candidate) ? 0.35 : 0));
            if (candScore > required) {
                out.pop();
                continue;
            }

            candidate = null;
            break;
        }

        if (candidate) out.push(candidate);
    }

    return out;
}

export interface V2LexicalTextEditorProps {
    words?: V2TranscriptWord[];
    title?: string;
    trackId?: string;
    readOnly?: boolean; // default true for now
    onDuplicateToNewComposition?: (startSec: number, endSec: number) => void;
    onTranscriptExporterReady?: (builder: TranscriptExporter | null) => void;
    className?: string;
    style?: React.CSSProperties;
}

const theme = {
    paragraph: "v2lexical-p",
    text: {
        bold: "v2lexical-text-bold",
        italic: "v2lexical-text-italic",
        underline: "v2lexical-text-underline",
    },
};

/**
 * Get a display name for a speaker.
 * Returns empty string if no explicit name is set (will show "New Speaker" placeholder).
 * No longer adds suffixes - speaker names are now entities set explicitly by the user.
 * 
 * @param explicitName - Name explicitly set (empty if not set)
 */
function getSpeakerDisplayName(explicitName: string): string {
    // Just return the explicit name - no auto-generated suffixes
    // Empty string will show "New Speaker" placeholder
    return explicitName || '';
}

// Module-level transcript cache - transcripts are immutable per trackId
// Key: `${projectId}:${transcriptId}`, Value: transcript JSON
const transcriptCache = new Map<string, any>();

type ProgressiveTranscriptCacheEntry = {
    manifest?: any;
    nextChunkIdToFetch: number;
    words: any[];
};

// Module-level progressive transcript cache (mutable while transcription is in progress)
// Key: `${projectId}:${transcriptId}`
const progressiveTranscriptCache = new Map<string, ProgressiveTranscriptCacheEntry>();

/**
 * Get transcript with caching. Now keyed by transcriptId instead of trackId
 * for proper cache invalidation when a track is re-transcribed.
 */
async function getCachedTranscript(
    GetTranscript: (params: { transcriptId: string; projectId: string }) => Promise<any>,
    transcriptId: string | undefined,
    projectId: string
): Promise<any | undefined> {
    if (!transcriptId) return undefined;

    const cacheKey = `${projectId}:${transcriptId}`;

    // Return from cache if available
    if (transcriptCache.has(cacheKey)) {
        return transcriptCache.get(cacheKey);
    }

    // Fetch and cache
    try {
        const transcript = await GetTranscript({ transcriptId, projectId });
        // Only cache "ready" transcripts that contain words.
        // During transcription, some backends may return placeholder payloads that would otherwise stick forever.
        if (transcript && Array.isArray(transcript.words) && transcript.words.length > 0) {
            transcriptCache.set(cacheKey, transcript);
        }
        return transcript;
    } catch {
        return undefined;
    }
}

async function getCachedProgressiveTranscript(
    GetTranscriptChunk: (params: { transcriptId: string; chunkId: number }) => Promise<any | undefined>,
    transcriptId: string | undefined,
    projectId: string,
    opts?: {
        maxChunksPerCall?: number;
    }
): Promise<{ words: any[]; manifest?: any } | undefined> {
    if (!transcriptId) return undefined;

    const maxChunksPerCall = opts?.maxChunksPerCall ?? 32;
    const cacheKey = `${projectId}:${transcriptId}`;

    let entry = progressiveTranscriptCache.get(cacheKey);
    if (!entry) {
        entry = { nextChunkIdToFetch: 0, words: [] };
        progressiveTranscriptCache.set(cacheKey, entry);
    }

    const isChunked = entry.manifest?.chunked === true;
    const publishedChunkCount = isChunked ? Number(entry.manifest?.publishedChunkCount) : Number.NaN;
    if (!Number.isFinite(publishedChunkCount) || publishedChunkCount <= 0) {
        return entry.words.length > 0 ? { words: entry.words, manifest: entry.manifest } : undefined;
    }

    let fetched = 0;
    while (entry.nextChunkIdToFetch < publishedChunkCount && fetched < maxChunksPerCall) {
        const chunkId = entry.nextChunkIdToFetch;
        let chunkJson: any | undefined;
        try {
            chunkJson = await GetTranscriptChunk({ transcriptId, chunkId });
        } catch {
            chunkJson = undefined;
        }

        if (!chunkJson || !Array.isArray(chunkJson.words)) {
            break;
        }

        entry.words.push(...chunkJson.words);
        entry.nextChunkIdToFetch += 1;
        fetched += 1;
    }

    return entry.words.length > 0 ? { words: entry.words, manifest: entry.manifest } : undefined;
}

const LoadWordsPlugin: React.FC<{ words?: V2TranscriptWord[] }> = observer(({words}) => {
    const [editor] = useLexicalComposerContext();
    const {GetTranscript, GetTranscriptChunk} = useAssetStore();
    
    // Access these observables to ensure MobX tracks them for reactivity
    const cutsVersion = ScriptLayerStore.cutsVersion;
    const scenesVersion = ScriptLayerStore.scenesVersion;
    const itemsLength = ScriptLayerStore.items.length;
    const transcriptHiddenRangesVersion = ScriptLayerStore.transcriptHiddenRangesVersion;
    const wordCorrectionsVersion = ScriptLayerStore.wordCorrectionsVersion;
    const transcriptParagraphBreaksVersion = ScriptLayerStore.transcriptParagraphBreaksVersion;
    const transcriptMarkersVersion = ScriptLayerStore.transcriptMarkersVersion;
    
    // Track filler word state for reactivity
    const fillerWordActive = FillerWordStore.isActive;
    const fillerWordCurrentIndex = FillerWordStore.currentIndex;
    const fillerWordOccurrencesCount = FillerWordStore.occurrences.length;
    
    // Track speaker overrides for reactivity
    const speakerOverridesVersion = ScriptLayerStore.speakerOverridesVersion;
    const speakerMuteRangesVersion = ScriptLayerStore.speakerMuteRangesVersion;
    const pendingSpeakerOverride = ScriptLayerStore.pendingSpeakerOverride;
    const showWordlessAudioEventsOnScript = Boolean(ProjectAssetsStore.projectShowWordlessAudioEventsOnScript);
    // Track project tracks/transcripts for reactivity (e.g., when transcription completes)
    const projectTracksVersion = ProjectAssetsStore.projectTracks
        .map((t: any) => `${t.id ?? t.trackId ?? ''}:${t.activeTranscriptId ?? ''}:${t.transcriptionState ?? ''}`)
        .join('|');

    const [transcriptDocTick, setTranscriptDocTick] = useState(0);
    const lastRenderKeyRef = useRef<string | null>(null);
    const transcriptUnsubsRef = useRef<Map<string, Unsubscribe>>(new Map());

    useEffect(() => {
        const projectId = ProjectAssetsStore.projectId as string | null;
        if (!projectId) return;

        if (!CompositionService.isInitialized()) return;

        const comp: any = CompositionService.composition;
        const scriptLayerInfo = comp?.scriptLayer;
        const scriptClipId = scriptLayerInfo?.clipId || null;
        if (!scriptClipId) return;

        const clip = CompositionService.getClipById(scriptClipId);
        if (!clip || !isScriptClip(clip)) return;

        const scriptClip = clip as ScriptLayerClip;
        const audioEntries = scriptClip.getAudioEntries();

        const nextTranscriptIds = new Set<string>();

        for (const entry of audioEntries) {
            const entryGain = Number(entry?.gain ?? 1);
            if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;
            const assetId = entry.assetId;
            if (!assetId) continue;
            const trackRecord = ProjectAssetsStore.projectTracks.find(
                (t: any) => t.id === assetId || t.trackId === assetId
            );
            const transcriptId = trackRecord?.activeTranscriptId;
            if (!transcriptId) continue;
            if (trackRecord && isTranscriptionInProgress(trackRecord.transcriptionState)) {
                nextTranscriptIds.add(String(transcriptId));
            }
        }

        // Tear down subscriptions that are no longer needed.
        for (const [transcriptId, unsub] of transcriptUnsubsRef.current.entries()) {
            if (!nextTranscriptIds.has(transcriptId)) {
                try { unsub(); } catch {}
                transcriptUnsubsRef.current.delete(transcriptId);
            }
        }

        // Set up new subscriptions.
        for (const transcriptId of nextTranscriptIds) {
            if (transcriptUnsubsRef.current.has(transcriptId)) continue;

            const transcriptRef = doc(db as any, kTranscriptsCollectionName, transcriptId);
            const unsub = onSnapshot(
                transcriptRef,
                (snap) => {
                    if (!snap.exists()) return;
                    const data = snap.data();

                    const cacheKey = `${projectId}:${transcriptId}`;
                    let entry = progressiveTranscriptCache.get(cacheKey);
                    if (!entry) {
                        entry = { nextChunkIdToFetch: 0, words: [] };
                        progressiveTranscriptCache.set(cacheKey, entry);
                    }
                    entry.manifest = data;

                    setTranscriptDocTick((t) => t + 1);
                },
                (err) => {
                    console.warn('[V2LexicalTextEditor] transcript Firestore subscription error', err);
                },
            );

            transcriptUnsubsRef.current.set(transcriptId, unsub);
        }

    }, [projectTracksVersion, CompositionStateStore.updateTimestamp, cutsVersion]);

    useEffect(() => {
        return () => {
            for (const [, unsub] of transcriptUnsubsRef.current.entries()) {
                try { unsub(); } catch {}
            }
            transcriptUnsubsRef.current.clear();
        };
    }, []);

	    useEffect(() => {
	        if (!CompositionService.isInitialized()) return;
	        const comp: any = CompositionService.composition;
	        const scriptLayerInfo = comp?.scriptLayer;
        
        // scriptLayer has { layerId, clipId } - clipId is the ScriptLayerClip's id
        const scriptClipId = scriptLayerInfo?.clipId || null;
        
        if (!scriptClipId && (!words || words.length === 0)) return;
        
	        let cancelled = false;
	        (async () => {
	            const projectId = ProjectAssetsStore.projectId as string;
	            const scrollContainer = (editor.getRootElement?.() ?? document.querySelector(".v2editor-scroll")) as HTMLElement | null;
	            const savedScrollTop = scrollContainer?.scrollTop ?? 0;

	            // Capture a stable caret boundary (between words) so we can restore selection after a full rebuild.
	            // Without this, root.clear() recreates nodes, and the browser scrolls to the new caret (often at top).
	            const isEditorFocused = (() => {
	                const rootEl = scrollContainer;
	                const active = document.activeElement as HTMLElement | null;
	                if (!rootEl || !active) return false;

	                // Treat the entire transcript editor area as "focused" so we keep caret stable even
	                // when an inline UI (e.g. marker rename input) is focused.
	                const transcriptRoot =
	                    (rootEl.closest(".v2editor-transcript-root") as HTMLElement | null) ??
	                    (document.querySelector(".v2editor-transcript-root") as HTMLElement | null);
	                if (transcriptRoot) {
	                    return transcriptRoot === active || transcriptRoot.contains(active);
	                }

	                return rootEl === active || rootEl.contains(active);
	            })();

	            let selectionBoundary: { before?: any; after?: any } | null = null;
	            if (isEditorFocused) {
	                try {
	                    editor.getEditorState().read(() => {
	                        const selection = $getSelection();
	                        if (!selection || !$isRangeSelection(selection)) return;

	                        const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
	                        const startNode = startPoint.getNode();

	                        // Find containing paragraph for cross-paragraph searches.
	                        let paragraphNode: any = startNode;
	                        while (
	                            paragraphNode &&
	                            typeof paragraphNode.getType === "function" &&
	                            paragraphNode.getType() !== "paragraph"
	                        ) {
	                            paragraphNode = paragraphNode.getParent?.();
	                        }

	                        const findFirstWord = (node: any): WordNode | null => {
	                            if (!node) return null;
	                            if ($isWordNode(node)) return node as WordNode;
	                            const first = node.getFirstChild?.();
	                            if (!first) return null;
	                            let child: any = first;
	                            while (child) {
	                                const found = findFirstWord(child);
	                                if (found) return found;
	                                child = child.getNextSibling?.();
	                            }
	                            return null;
	                        };

	                        const findLastWord = (node: any): WordNode | null => {
	                            if (!node) return null;
	                            if ($isWordNode(node)) return node as WordNode;
	                            const last = node.getLastChild?.();
	                            if (!last) return null;
	                            let child: any = last;
	                            while (child) {
	                                const found = findLastWord(child);
	                                if (found) return found;
	                                child = child.getPreviousSibling?.();
	                            }
	                            return null;
	                        };

	                        const findPrevWordInDoc = (node: any): WordNode | null => {
	                            let sibling: any = node?.getPreviousSibling?.();
	                            while (sibling) {
	                                const found = findLastWord(sibling);
	                                if (found) return found;
	                                sibling = sibling.getPreviousSibling?.();
	                            }
	                            let p: any = paragraphNode?.getPreviousSibling?.();
	                            while (p) {
	                                const found = findLastWord(p);
	                                if (found) return found;
	                                p = p.getPreviousSibling?.();
	                            }
	                            return null;
	                        };

	                        const findNextWordInDoc = (node: any): WordNode | null => {
	                            let sibling: any = node?.getNextSibling?.();
	                            while (sibling) {
	                                const found = findFirstWord(sibling);
	                                if (found) return found;
	                                sibling = sibling.getNextSibling?.();
	                            }
	                            let p: any = paragraphNode?.getNextSibling?.();
	                            while (p) {
	                                const found = findFirstWord(p);
	                                if (found) return found;
	                                p = p.getNextSibling?.();
	                            }
	                            return null;
	                        };

	                        let prevWord: WordNode | null = null;
	                        let nextWord: WordNode | null = null;

	                        if ($isWordNode(startNode)) {
	                            const wordNode = startNode as WordNode;
	                            const size = wordNode.getTextContentSize();
	                            const atEnd = startPoint.offset >= size;

	                            if (atEnd) {
	                                prevWord = wordNode;
	                                nextWord = findNextWordInDoc(wordNode);
	                            } else {
	                                nextWord = wordNode;
	                                prevWord = findPrevWordInDoc(wordNode);
	                            }
	                        } else {
	                            prevWord = findPrevWordInDoc(startNode);
	                            nextWord = findNextWordInDoc(startNode);
	                        }

	                        if (!prevWord && !nextWord) return;
	                        selectionBoundary = {
	                            before: prevWord ? (prevWord as any).__payload : undefined,
	                            after: nextWord ? (nextWord as any).__payload : undefined,
	                        };
	                    });
	                } catch {}
	            }

	            const allTrackedWords: TrackedWord[] = [];
                let shouldResolveOverlaps = false;
            
            // Get the script layer clip directly
            if (scriptClipId) {
                const clip = CompositionService.getClipById(scriptClipId);
                
                if (clip && isScriptClip(clip)) {
                    const scriptClip = clip as ScriptLayerClip;
                    // Use getAudioEntries() to get range and offset info for each source
                    const audioEntries = scriptClip.getAudioEntries();
                    shouldResolveOverlaps = Array.isArray(audioEntries) && audioEntries.length >= 2;
                    const names = scriptClip.names || [];
                    const colors = scriptClip.colors || [];

                    // When multiple sources are present and all sources share a single color (common when
                    // clips are created before per-source colors are assigned), derive per-source colors
                    // from DEFAULT_SPEAKER_COLORS so transcript + waveform stay distinct as tracks transcribe.
                    const normalizedSourceColors = [
                        ...(Array.isArray(colors) ? colors : []),
                        ...(Array.isArray(audioEntries) ? audioEntries.map((e: any) => e?.color) : []),
                    ]
                        .map((c: any) => (typeof c === "string" ? c.trim().toLowerCase() : ""))
                        .filter((c: string) => c.length > 0);
                    const hasDistinctSourceColors = new Set(normalizedSourceColors).size > 1;
                    const shouldAutoAssignSourceColors =
                        shouldResolveOverlaps &&
                        !hasDistinctSourceColors &&
                        (normalizedSourceColors.length === 0 ||
                            normalizedSourceColors.every((c) => c === DEFAULT_SPEAKER_COLORS[0].toLowerCase()));
                    
                    // Fetch transcripts for each audio source
                    for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
                        const entry = audioEntries[sourceIndex];
                        const entryGain = Number((entry as any)?.gain ?? 1);
                        if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;
                        const assetId = entry.assetId;
                        if (!assetId) continue;
                        
                        // Only use explicit names from ScriptLayerClip.names - no auto-generated names
                        // Empty string will show "New Speaker" placeholder, prompting user to set speaker
                        const explicitName = names[sourceIndex] || '';
                        const sourceName = explicitName; // No filename fallback - just use explicit name
                        const sourceColor = shouldAutoAssignSourceColors
                            ? DEFAULT_SPEAKER_COLORS[sourceIndex % DEFAULT_SPEAKER_COLORS.length]
                            : colors[sourceIndex] || entry.color || DEFAULT_SPEAKER_COLORS[sourceIndex % DEFAULT_SPEAKER_COLORS.length];
                        
	                        // Get source duration for range defaults
		                        const trackRecord = ProjectAssetsStore.projectTracks.find(
		                            (t: any) => t.id === assetId || t.trackId === assetId
		                        );
		                        const sourceDuration = trackRecord?.duration ?? 0;

	                        // Support per-source multi-segment placement (empty scenes / sequential sources).
	                        // Fallback to legacy single-segment fields.
	                        const entrySegments: Array<{
	                            offsetSec: number;
	                            playbackRate: number;
	                            rangeInSec: number;
	                            rangeOutSec: number;
	                        }> = [];

	                        const segmentsRaw: any[] =
	                            Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0
	                                ? ((entry as any).segments as any[])
	                                : [];

	                        if (segmentsRaw.length > 0) {
	                            for (const seg of segmentsRaw) {
	                                const segOffset = Number(seg?.offsetSec ?? 0);
	                                const segRate = Number(seg?.playbackRate ?? (entry as any)?.playbackRate ?? 1.0) || 1.0;
	                                const segIn = Number(seg?.range?.inSec ?? 0);
	                                const segOut = Number(seg?.range?.outSec ?? 0);
	                                if (!Number.isFinite(segOffset) || !Number.isFinite(segRate) || segRate <= 0) continue;
	                                if (!Number.isFinite(segIn) || !Number.isFinite(segOut) || segOut <= segIn) continue;
	                                entrySegments.push({
	                                    offsetSec: segOffset,
	                                    playbackRate: segRate,
	                                    rangeInSec: segIn,
	                                    rangeOutSec: segOut,
	                                });
	                            }
	                        } else {
	                            const range = (entry as any).range;
	                            const offsetSec = Number((entry as any).offsetSec ?? 0) || 0;
	                            const playbackRate = Number((entry as any).playbackRate ?? 1.0) || 1.0;
	                            const rangeInSec = Number(range?.inSec ?? 0);
	                            const rangeOutSec = Number(range?.outSec ?? sourceDuration);
	                            entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
	                        }

                            const transcriptId = trackRecord?.activeTranscriptId;
                            if (!transcriptId) continue;
                            const transcriptJson =
                                trackRecord && isTranscriptionInProgress(trackRecord.transcriptionState)
                                    ? await getCachedProgressiveTranscript(
                                          GetTranscriptChunk,
                                          transcriptId,
                                          projectId,
                                          { maxChunksPerCall: 32 }
                                      )
                                    : await getCachedTranscript(GetTranscript, transcriptId, projectId);

                            if (cancelled) return;

                        if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) {
                            continue;
                        }
                        
	                        // Create tracked words (filtered and adjusted)
	                        const MIN_ENERGY_RATIO = 0.2;
	                        for (const seg of entrySegments) {
                            for (const w of transcriptJson.words) {
                                const tokenKind = String((w as any)?.type ?? "word").trim() || "word";
                                const includeToken =
                                    tokenKind === "word" ||
                                    (showWordlessAudioEventsOnScript && tokenKind === "audio_event");
                                if (!includeToken) continue;

	                                // Filter out faint words if energy data is present
	                                if (tokenKind === "word" && w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) {
	                                    continue;
	                                }

	                                // Support both t0/t1 (new) and start/end (legacy) field names
	                                const wordStart = Number(w.t0 ?? w.start ?? 0);
	                                const wordEnd = Number(w.t1 ?? w.end ?? wordStart);

	                                // Only include words within this segment's source range
	                                if (wordStart < seg.rangeInSec || wordEnd > seg.rangeOutSec) continue;

	                                // Support both speakerId (new) and speaker_id (legacy) field names
	                                // ElevenLabs returns speaker IDs as strings like "speaker_0"
	                                const speakerIdRaw = w.speakerId ?? w.speaker_id;
	                                const speakerId = speakerIdRaw != null ? String(speakerIdRaw) : undefined;
	                                const displayName = getSpeakerDisplayName(explicitName);

	                                // Adjust times: subtract range start, divide by playbackRate, add timeline offset
	                                const adjustedStart = (wordStart - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
	                                const adjustedEnd = (wordEnd - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;

                                    const correctedText =
                                        tokenKind === "word"
                                            ? (
                                                ScriptLayerStore.getWordCorrectionText({
                                                    sourceIndex,
                                                    srcStartSec: wordStart,
                                                    srcEndSec: wordEnd,
                                                }) ?? String(w.text ?? "").trim()
                                            )
                                            : String(w.text ?? "").trim();

                                    if (!correctedText) continue;

                                    const energyRatioRaw = Number((w as any)?.energy?.ratio ?? (w as any)?.energyRatio ?? (w as any)?.energy_ratio ?? NaN);
                                    const energyRatio = Number.isFinite(energyRatioRaw) ? energyRatioRaw : undefined;

	                                allTrackedWords.push({
	                                    text: correctedText,
                                        kind: tokenKind,
	                                    startSec: adjustedStart,
	                                    endSec: adjustedEnd,
	                                    // Also store original source times for cutting
	                                    srcStartSec: wordStart,
	                                    srcEndSec: wordEnd,
	                                    speaker: displayName,
	                                    sourceIndex,
	                                    speakerId,
	                                    sourceName,
	                                    sourceColor,
                                        sourceGain: entryGain,
                                        energyRatio,
	                                });
                            }
                        }
                    }
                }
            }
	            
	            // Sort all words by start time
	            allTrackedWords.sort((a, b) => a.startSec - b.startSec);

                // Clamp implausible word durations so a single token can't "cover" tens of seconds.
                // We cap to a small inferred duration (or the next word start, whichever is earlier).
                for (let i = 0; i < allTrackedWords.length; i++) {
                    const w = allTrackedWords[i];
                    const start = Number(w.startSec ?? 0);
                    if (!Number.isFinite(start)) continue;
                    const nextStartRaw = allTrackedWords[i + 1]?.startSec;
                    const nextStart = Number.isFinite(Number(nextStartRaw)) ? Number(nextStartRaw) : NaN;

                    let end = Number(w.endSec ?? start);
                    if (!Number.isFinite(end) || end <= start) {
                        end = start + DEFAULT_WORD_DURATION_SEC;
                    }

                    const dur = end - start;
                    if (!Number.isFinite(dur) || dur > MAX_WORD_DURATION_SEC) {
                        end = start + MAX_INFERRED_WORD_DURATION_SEC;
                    }

                    if (Number.isFinite(nextStart) && nextStart > start) {
                        end = Math.min(end, nextStart);
                    }

                    if (end < start) end = start;
                    w.endSec = end;
                }

	            // Filter and mark words based on cuts
	            // - Words in "delete" cuts are removed
	            // - Words in strikethrough modes are kept but marked for styling
	            const visibleWordsBeforeOverlap: (TrackedWord & { isStrikethrough?: boolean; isRetakeStrikethrough?: boolean })[] = [];
            
            for (const w of allTrackedWords) {
                const wordTime = w.startSec ?? 0;
                
                // Check if word is in a "delete" cut (should be removed)
                if (ScriptLayerStore.isTimeInDeleteCut(wordTime)) {
                    continue; // Skip this word
                }
                
                // Check if word is in a gap (should be removed - gap replaces with silence)
                if (ScriptLayerStore.isTimeInGap(wordTime)) {
                    continue; // Skip this word
                }

                // Check if word is hidden from transcript only (should be removed from view).
                if (ScriptLayerStore.isTimeInTranscriptHiddenRange(wordTime)) {
                    continue;
                }
                
                // Check if word is in a strikethrough-style cut (keep but mark).
                const cutMode = ScriptLayerStore.getCutTranscriptModeAtTime(wordTime);
                const isRetakeStrikethrough = cutMode === "retake-strikethrough";
                const isStrikethrough = cutMode === "strikethrough" || isRetakeStrikethrough;
                visibleWordsBeforeOverlap.push({ ...w, isStrikethrough, isRetakeStrikethrough });
            }

            const visibleWords: (TrackedWord & { isStrikethrough?: boolean; isRetakeStrikethrough?: boolean })[] =
                shouldResolveOverlaps
                    ? resolveOverlappingTranscriptWords(visibleWordsBeforeOverlap)
                    : visibleWordsBeforeOverlap;

            // Canonical speaker resolution used by BOTH transcript rendering and waveform coloring.
            // Transcript may choose to suppress overlaps; waveform may choose to keep them, but speaker
            // identity/color assignment should always come from this single path.
            const speakerOverrides = ScriptLayerStore.speakerOverrides;
            const pendingOverride = ScriptLayerStore.pendingSpeakerOverride;

            const overrideSpecificity = (o: SpeakerOverride): number => {
                let score = 0;
                if (o.sourceIndex != null) score += 1;
                if (o.diarizationSpeakerId) score += 2;
                return score;
            };

            const overrideDuration = (o: SpeakerOverride): number => {
                const start = Number(o.startSec ?? 0);
                const end = Number(o.endSec ?? 0);
                return Math.max(0, end - start);
            };

            const chooseBetterOverride = (a: SpeakerOverride, b: SpeakerOverride): SpeakerOverride => {
                const durA = overrideDuration(a);
                const durB = overrideDuration(b);
                if (durA !== durB) return durB < durA ? b : a;

                const specA = overrideSpecificity(a);
                const specB = overrideSpecificity(b);
                if (specA !== specB) return specB > specA ? b : a;

                if (a.startSec !== b.startSec) return b.startSec > a.startSec ? b : a;
                return b.id > a.id ? b : a;
            };

            const getEffectiveSpeaker = (w: TrackedWord): {
                speakerKey: string;
                speaker: string;
                color: string;
                overrideId?: string;
                isPending?: boolean;
                firestoreSpeakerId?: string;
            } => {
                const wordTime = w.startSec ?? 0;
                const wordDiarizationId = w.speakerId != null ? String(w.speakerId) : undefined;

                if (pendingOverride && wordTime >= pendingOverride.startSec && wordTime < pendingOverride.endSec) {
                    const usedColors = new Set<string>();
                    for (const vw of visibleWords) {
                        if (vw.sourceColor) usedColors.add(vw.sourceColor);
                    }
                    for (const o of speakerOverrides) {
                        if (o.color) usedColors.add(o.color);
                    }
                    const newColor = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));
                    return {
                        speakerKey: 'pending-override',
                        speaker: '',
                        color: newColor,
                        isPending: true,
                    };
                }

                const matchingOverrides = speakerOverrides.filter((override) => {
                    if (wordTime < override.startSec || wordTime >= override.endSec) return false;
                    if (override.sourceIndex != null && override.sourceIndex !== w.sourceIndex) return false;
                    if (override.diarizationSpeakerId && override.diarizationSpeakerId !== wordDiarizationId) return false;
                    return true;
                });

                if (matchingOverrides.length) {
                    const override = matchingOverrides.reduce(chooseBetterOverride);
                    const speakerKey = override.speakerId ? `speaker-${override.speakerId}` : `override-${override.id}`;
                    return {
                        speakerKey,
                        speaker: override.speakerName,
                        color: override.color || w.sourceColor,
                        overrideId: override.id,
                        firestoreSpeakerId: override.speakerId,
                    };
                }

                let speakerColor = w.sourceColor;
                // For single-source scripts, use diarization palette so different speakers are visible.
                // For multi-source scripts, keep stable per-source colors.
                if (!shouldResolveOverlaps && w.speakerId != null) {
                    const speakerIdStr = String(w.speakerId);
                    const match = speakerIdStr.match(/(\d+)$/);
                    if (match) {
                        const speakerNum = parseInt(match[1], 10);
                        speakerColor = DEFAULT_SPEAKER_COLORS[speakerNum % DEFAULT_SPEAKER_COLORS.length];
                    }
                }

                return {
                    speakerKey: `source-${w.sourceIndex}-${w.speakerId ?? 'default'}`,
                    speaker: w.speaker || w.sourceName,
                    color: speakerColor,
                };
            };

	            // Save scroll position before update (used even when editor isn't focused)
	            const scrollTop = savedScrollTop;

            // Avoid unnecessary full Lexical rebuilds while polling if nothing changed.
            const keyFirst = visibleWords[0]?.startSec ?? 0;
            const keyLast = visibleWords[visibleWords.length - 1]?.endSec ?? visibleWords[visibleWords.length - 1]?.startSec ?? 0;
            const rawKeyFirst = visibleWordsBeforeOverlap[0]?.startSec ?? 0;
            const rawKeyLast =
                visibleWordsBeforeOverlap[visibleWordsBeforeOverlap.length - 1]?.endSec ??
                visibleWordsBeforeOverlap[visibleWordsBeforeOverlap.length - 1]?.startSec ??
                0;
            const pendingStart = pendingSpeakerOverride?.startSec ?? "";
            const pendingEnd = pendingSpeakerOverride?.endSec ?? "";
            const nextRenderKey = [
                visibleWords.length,
                keyFirst,
                keyLast,
                visibleWordsBeforeOverlap.length,
                rawKeyFirst,
                rawKeyLast,
                itemsLength,
                cutsVersion,
                scenesVersion,
                transcriptHiddenRangesVersion,
                wordCorrectionsVersion,
                transcriptParagraphBreaksVersion,
                transcriptMarkersVersion,
                fillerWordActive ? 1 : 0,
                fillerWordCurrentIndex,
                fillerWordOccurrencesCount,
                speakerOverridesVersion,
                speakerMuteRangesVersion,
                showWordlessAudioEventsOnScript ? 1 : 0,
                pendingStart,
                pendingEnd,
                CompositionStateStore.updateTimestamp,
            ].join("|");
            if (lastRenderKeyRef.current === nextRenderKey) {
                return;
            }
            lastRenderKeyRef.current = nextRenderKey;

            if (cancelled) return;

            // Speaker-colored waveform segments (ScriptClip only).
            // These are derived from transcript word timings, mapped into timeline time using ScriptLayerStore.
            try {
                const EPS = 1e-3;
                const roundSec = (v: number) => Math.round(v * 1000) / 1000;
                const MIN_COLOR_SLIVER_SEC = 0.09;

                const parseCssColorToRgb = (color: string): { r: number; g: number; b: number } | null => {
                    const s = String(color ?? "").trim();
                    if (!s) return null;
                    if (s.startsWith("#")) {
                        const hex = s.slice(1);
                        if (hex.length === 3) {
                            const r = parseInt(hex[0] + hex[0], 16);
                            const g = parseInt(hex[1] + hex[1], 16);
                            const b = parseInt(hex[2] + hex[2], 16);
                            if ([r, g, b].every(Number.isFinite)) return { r, g, b };
                            return null;
                        }
                        if (hex.length >= 6) {
                            const r = parseInt(hex.slice(0, 2), 16);
                            const g = parseInt(hex.slice(2, 4), 16);
                            const b = parseInt(hex.slice(4, 6), 16);
                            if ([r, g, b].every(Number.isFinite)) return { r, g, b };
                            return null;
                        }
                        return null;
                    }

                    const m = s.match(/rgba?\(([^)]+)\)/i);
                    if (!m) return null;
                    const parts = m[1].split(",").map((p) => Number.parseFloat(p.trim()));
                    if (parts.length < 3) return null;
                    const [r, g, b] = parts;
                    if (![r, g, b].every(Number.isFinite)) return null;
                    return {
                        r: Math.max(0, Math.min(255, Math.round(r))),
                        g: Math.max(0, Math.min(255, Math.round(g))),
                        b: Math.max(0, Math.min(255, Math.round(b))),
                    };
                };

                const blendTwoColors = (a: string, b: string): string => {
                    const rgbA = parseCssColorToRgb(a);
                    const rgbB = parseCssColorToRgb(b);
                    if (!rgbA || !rgbB) return a;
                    return `rgba(${Math.round((rgbA.r + rgbB.r) / 2)}, ${Math.round((rgbA.g + rgbB.g) / 2)}, ${Math.round((rgbA.b + rgbB.b) / 2)}, 0.9)`;
                };

                // Map clip-local times → timeline times efficiently using the current script segments.
                // This avoids O(words * segments) behavior when there are lots of cuts.
                const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
                const clipForMap: any = scriptClipId ? CompositionService.getClipById(scriptClipId) : null;
                const clipStartSec = Number(clipForMap?.start?.seconds ?? 0);
                const segs: any[] =
                    Array.isArray(ScriptLayerStore.segments) && ScriptLayerStore.segments.length > 0
                        ? (ScriptLayerStore.segments as any[])
                        : Array.isArray(clipForMap?.scriptSegments)
                          ? (clipForMap.scriptSegments as any[])
                          : [];
                let segIdx = 0;

                const mapStartClipTimeToTimeline = (clipTimeSec: number): { timelineSec: number; seg: any } | null => {
                    if (!Number.isFinite(clipTimeSec)) return null;
                    if (!segs.length) {
                        const mapped = ScriptLayerStore.mapSourceTimeToCompositionTime(clipTimeSec);
                        if (mapped == null || !Number.isFinite(mapped)) return null;
                        return { timelineSec: mapped, seg: null };
                    }

                    while (segIdx < segs.length) {
                        const segEnd = Number(segs[segIdx]?.srcEndSec ?? NaN);
                        if (!Number.isFinite(segEnd)) {
                            segIdx++;
                            continue;
                        }
                        if (clipTimeSec >= segEnd - EPS) {
                            segIdx++;
                            continue;
                        }
                        break;
                    }

                    const seg = segIdx < segs.length ? segs[segIdx] : null;
                    if (!seg) return null;

                    const segStart = Number(seg?.srcStartSec ?? NaN);
                    const segCompStart = Number(seg?.compStartSec ?? NaN);
                    if (!Number.isFinite(segStart) || !Number.isFinite(segCompStart)) return null;
                    if (clipTimeSec < segStart - EPS) return null;

                    const absComp = segCompStart + (clipTimeSec - segStart);
                    const mapped = (absComp - clipStartSec) / rate;
                    if (!Number.isFinite(mapped)) return null;
                    return { timelineSec: mapped, seg };
                };

                type WaveformInterval = {
                    id: number;
                    startSec: number;
                    endSec: number;
                    color: string;
                    sourceIndex: number;
                    strength: number;
                };
                const waveformIntervals: WaveformInterval[] = [];
                let nextIntervalId = 1;

                // Keep overlap words for waveform coloring (so crosstalk can be represented), but
                // resolve speaker identity/color through the same canonical resolver as transcript.
                for (const w of visibleWordsBeforeOverlap) {
                    if (w.isStrikethrough || w.isRetakeStrikethrough) continue; // audio removed (cut collapsed)

                    const startClip = Number(w.startSec ?? NaN);
                    const endClipRaw = Number(w.endSec ?? w.startSec ?? NaN);
                    if (!Number.isFinite(startClip) || !Number.isFinite(endClipRaw)) continue;
                    const endClip = Math.max(startClip, endClipRaw);
                    if (!(endClip - startClip > EPS)) continue;

                    const effective = getEffectiveSpeaker(w);
                    const mappedStart = mapStartClipTimeToTimeline(startClip);
                    if (!mappedStart) continue;

                    const startTimeline = mappedStart.timelineSec;

                    let endTimeline: number | null = null;
                    if (mappedStart.seg) {
                        const segStart = Number(mappedStart.seg?.srcStartSec ?? NaN);
                        const segEnd = Number(mappedStart.seg?.srcEndSec ?? NaN);
                        const segCompStart = Number(mappedStart.seg?.compStartSec ?? NaN);
                        if (Number.isFinite(segStart) && Number.isFinite(segEnd) && Number.isFinite(segCompStart)) {
                            const endClipForMap = Math.max(startClip, Math.min(endClip, segEnd - EPS));
                            const absCompEnd = segCompStart + (endClipForMap - segStart);
                            endTimeline = (absCompEnd - clipStartSec) / rate;
                        }
                    }
                    if (endTimeline == null || !Number.isFinite(endTimeline)) {
                        endTimeline =
                            ScriptLayerStore.mapSourceTimeToCompositionTime(Math.max(startClip, endClip - EPS)) ??
                            ScriptLayerStore.mapSourceTimeToCompositionTime(endClip);
                    }
                    if (endTimeline == null || !Number.isFinite(endTimeline)) continue;

                    const s = roundSec(Math.max(0, startTimeline));
                    const e = roundSec(Math.max(s, endTimeline));
                    if (!(e - s > EPS)) continue;

                    const strengthRaw = Number(scoreForOverlapResolution(w));
                    const strength = Number.isFinite(strengthRaw) && strengthRaw > 0 ? strengthRaw : 1;
                    const sourceIndex = Number.isFinite(Number(w.sourceIndex)) ? Number(w.sourceIndex) : -1;

                    waveformIntervals.push({
                        id: nextIntervalId++,
                        startSec: s,
                        endSec: e,
                        color: effective.color,
                        sourceIndex,
                        strength,
                    });
                }

                type Event = { t: number; kind: 0 | 1; id: number };
                const events: Event[] = [];
                const intervalById = new Map<number, WaveformInterval>();
                for (const interval of waveformIntervals) {
                    if (!(interval.endSec - interval.startSec > EPS)) continue;
                    intervalById.set(interval.id, interval);
                    events.push({ t: interval.startSec, kind: 1, id: interval.id }); // start
                    events.push({ t: interval.endSec, kind: 0, id: interval.id }); // end
                }

                if (events.length === 0) {
                    ScriptLayerStore.setSpeakerWaveformSegments([]);
                } else {
                    events.sort((a, b) => (a.t !== b.t ? a.t - b.t : a.kind - b.kind)); // end before start

                    const active = new Map<number, WaveformInterval>();
                    const out: SpeakerWaveformColorSegment[] = [];

                    const chooseDominant = (activeIntervals: WaveformInterval[]): WaveformInterval => {
                        let best = activeIntervals[0];
                        for (let i = 1; i < activeIntervals.length; i++) {
                            const candidate = activeIntervals[i];
                            if (candidate.strength > best.strength + EPS) {
                                best = candidate;
                                continue;
                            }
                            if (Math.abs(candidate.strength - best.strength) <= EPS) {
                                const bestDur = best.endSec - best.startSec;
                                const candDur = candidate.endSec - candidate.startSec;
                                if (candDur > bestDur + EPS) {
                                    best = candidate;
                                    continue;
                                }
                                if (Math.abs(candDur - bestDur) <= EPS && candidate.id < best.id) {
                                    best = candidate;
                                }
                            }
                        }
                        return best;
                    };

                    const pickColorForSlice = (activeIntervals: WaveformInterval[]): string | null => {
                        if (!activeIntervals.length) return null;
                        if (activeIntervals.length === 1) return activeIntervals[0].color;

                        const bySource = new Map<number, WaveformInterval[]>();
                        for (const interval of activeIntervals) {
                            const key = Number.isFinite(interval.sourceIndex) ? interval.sourceIndex : -1;
                            const list = bySource.get(key) ?? [];
                            list.push(interval);
                            bySource.set(key, list);
                        }

                        // Same-source overlap (diarization jitter): choose one dominant color, no blending.
                        if (bySource.size <= 1) {
                            return chooseDominant(activeIntervals).color;
                        }

                        // Cross-source crosstalk: blend dominant colors from the two strongest active sources.
                        const dominantPerSource: WaveformInterval[] = [];
                        for (const intervals of bySource.values()) {
                            dominantPerSource.push(chooseDominant(intervals));
                        }
                        dominantPerSource.sort((a, b) => b.strength - a.strength);

                        const primary = dominantPerSource[0];
                        const secondary = dominantPerSource[1] ?? dominantPerSource[0];
                        if (!primary || !secondary) return null;
                        if (primary.id === secondary.id) return primary.color;
                        return blendTwoColors(primary.color, secondary.color);
                    };

                    let prevT = events[0].t;
                    let i = 0;
                    while (i < events.length) {
                        const t = events[i].t;
                        if (t > prevT + EPS && active.size > 0) {
                            const activeIntervals = Array.from(active.values());
                            const color = pickColorForSlice(activeIntervals);
                            if (color) {
                                out.push({ startSec: prevT, endSec: t, color });
                            }
                        }

                        while (i < events.length && events[i].t === t) {
                            const ev = events[i];
                            if (ev.kind === 0) {
                                active.delete(ev.id);
                            } else {
                                const interval = intervalById.get(ev.id);
                                if (interval) active.set(ev.id, interval);
                            }
                            i++;
                        }

                        prevT = t;
                    }

                    // Coalesce adjacent segments with identical colors.
                    const mergedOut: SpeakerWaveformColorSegment[] = [];
                    for (const seg of out) {
                        if (!(seg.endSec - seg.startSec > EPS)) continue;
                        const last = mergedOut[mergedOut.length - 1];
                        if (last && last.color === seg.color && Math.abs(last.endSec - seg.startSec) <= EPS) {
                            last.endSec = seg.endSec;
                        } else {
                            mergedOut.push({ ...seg });
                        }
                    }

                    // Smooth tiny one-off color spikes (common from diarization jitter/crosstalk micro-overlaps).
                    // If a very short segment is sandwiched by the same color, fold it into neighbors.
                    const smoothed: SpeakerWaveformColorSegment[] = [];
                    for (let idx = 0; idx < mergedOut.length; idx++) {
                        const current = mergedOut[idx];
                        const prev = smoothed.length > 0 ? smoothed[smoothed.length - 1] : null;
                        const next = idx + 1 < mergedOut.length ? mergedOut[idx + 1] : null;
                        const duration = current.endSec - current.startSec;

                        if (
                            prev &&
                            next &&
                            duration <= MIN_COLOR_SLIVER_SEC &&
                            prev.color === next.color &&
                            Math.abs(prev.endSec - current.startSec) <= EPS &&
                            Math.abs(current.endSec - next.startSec) <= EPS
                        ) {
                            prev.endSec = next.endSec;
                            idx++;
                            continue;
                        }

                        smoothed.push({ ...current });
                    }

                    ScriptLayerStore.setSpeakerWaveformSegments(smoothed);
                }
            } catch {
                // Best-effort: fall back to single-color waveform.
                ScriptLayerStore.clearSpeakerWaveformSegments();
            }

	            editor.update(() => {
	                const root = $getRoot();
	                root.clear();
                
                // Legacy fallback: only use the pre-parsed `words` prop when we could not build a
                // segment-aware transcript (e.g. transcript fetch not available yet) AND there
                // are no script edits that would change visibility (cuts/gaps).
                //
                // Important: when cuts/gaps remove all words, we must render empty scenes ("/")
                // instead of falling back to the raw transcript.
                const hasScriptEdits =
                    (ScriptLayerStore.cuts?.length ?? 0) > 0 ||
                    (ScriptLayerStore.gaps?.length ?? 0) > 0 ||
                    (ScriptLayerStore.transcriptHiddenRanges?.length ?? 0) > 0 ||
                    (ScriptLayerStore.wordCorrections?.length ?? 0) > 0 ||
                    (ScriptLayerStore.transcriptParagraphBreaks?.length ?? 0) > 0 ||
                    (ScriptLayerStore.transcriptMarkers?.length ?? 0) > 0;
                if (allTrackedWords.length === 0 && !hasScriptEdits && words && words.length > 0) {
                    // Fallback to provided words prop (legacy behavior)
                            const pNode = $createParagraphNode();
                    words.forEach((w, idx) => {
                                const node = new WordNode(w.text, {
                                    kind: w.kind,
                                    startSec: w.startSec,
                                    endSec: w.endSec,
                                    sourceIndex: w.sourceIndex,
                                    speaker: w.speaker,
                                });
                                pNode.append(node);
                        const needsSpace = !!w.text && !/^[.,!?;:)\]]$/.test(w.text) && idx !== (words.length - 1);
                                if (needsSpace) pNode.append($createTextNode(" "));
                            });
                            root.append(pNode);
                    return;
                }
                
		                // Scene-aware transcript rendering:
		                // - Empty scenes render as "/" on their own line.
		                // - Non-empty scene boundaries render "/" inline (at the start of the scene).
		                // - Speaker segmentation is applied within each scene.
	                //
	                // Note: Scenes are derived from ScriptLayerStore.splits (scene boundaries) and clip duration.
	                // Empty scenes are simply scenes with no visible words.
	                const scenesForRender = Array.isArray(ScriptLayerStore.scenes) ? ScriptLayerStore.scenes : [];
	                
	                // Step 1: Group consecutive words by speaker into "speaker segments"
	                // Apply speaker overrides: if a word falls within an override range, use that speaker
	                type SpeakerSegment = {
	                    speakerKey: string;
	                    sourceIndex: number;
	                    speakerId?: string | number;  // ElevenLabs returns strings like "speaker_0"
	                    speaker: string;
                    sourceName: string;
                    sourceColor: string;
                    words: TrackedWord[];
                    overrideId?: string; // If this segment is from a speaker override
                    firestoreSpeakerId?: string; // Firestore speaker entity ID for merging
                };
		                const renderEmptySceneLine = (payload: { sceneId: string; sceneIndex: number; startSec: number; endSec: number }) => {
		                    const pNode = $createParagraphNode();
		                    pNode.append($createSceneBreakNode(payload));
		                    root.append(pNode);
		                };

                    // Transcript markers (chapters/headers) - transcript-only navigation.
                    const transcriptMarkers = Array.isArray(ScriptLayerStore.transcriptMarkers)
                        ? (ScriptLayerStore.transcriptMarkers as any[])
                        : [];
                    const markersForRender = transcriptMarkers
                        .map((m) => ({
                            id: String(m?.id ?? ""),
                            title: String(m?.title ?? "").trim(),
                            after: m?.after,
                            before: m?.before,
                            timeSec: Number.isFinite(m?.timeSec) ? Number(m.timeSec) : undefined,
                        }))
                        .filter((m) => m.id && m.title);
                    markersForRender.sort((a, b) => Number(a.timeSec ?? 0) - Number(b.timeSec ?? 0));

                    const insertedMarkerIds = new Set<string>();
                    let lastRenderedWordEndSec: number | null = null;

                    const anchorMatchesWord = (anchor: any, w: V2TranscriptWord, segSourceIndex: number): boolean => {
                        if (!anchor || typeof anchor !== "object") return false;
                        const EPS = 1e-3;

                        const aSourceIndex = Number.isFinite(anchor?.sourceIndex) ? Number(anchor.sourceIndex) : undefined;
                        if (aSourceIndex != null && aSourceIndex !== segSourceIndex) return false;

                        const aSrcStart = Number.isFinite(anchor?.srcStartSec) ? Number(anchor.srcStartSec) : undefined;
                        const aSrcEnd = Number.isFinite(anchor?.srcEndSec) ? Number(anchor.srcEndSec) : undefined;
                        const aTime = Number.isFinite(anchor?.timeSec) ? Number(anchor.timeSec) : undefined;

                        const wSrcStart = Number.isFinite((w as any)?.srcStartSec) ? Number((w as any).srcStartSec) : undefined;
                        const wSrcEnd = Number.isFinite((w as any)?.srcEndSec) ? Number((w as any).srcEndSec) : undefined;
                        const wStart = Number.isFinite((w as any)?.startSec) ? Number((w as any).startSec) : undefined;
                        const wEnd = Number.isFinite((w as any)?.endSec)
                            ? Number((w as any).endSec)
                            : Number.isFinite((w as any)?.startSec)
                              ? Number((w as any).startSec)
                              : undefined;

                        const hasStable = aSrcStart != null || aSrcEnd != null || aTime != null;
                        if (!hasStable) return false;

                        if (aSrcStart != null) {
                            if (wSrcStart == null || Math.abs(wSrcStart - aSrcStart) > EPS) return false;
                        }
                        if (aSrcEnd != null) {
                            if (wSrcEnd == null || Math.abs(wSrcEnd - aSrcEnd) > EPS) return false;
                        }
                        if (aTime != null) {
                            const matchesStart = wStart != null && Math.abs(wStart - aTime) <= EPS;
                            const matchesEnd = wEnd != null && Math.abs(wEnd - aTime) <= EPS;
                            if (!matchesStart && !matchesEnd) return false;
                        }

                        return true;
                    };

                    const insertMarker = (m: any, timeSec: number) => {
                        if (!m?.id || insertedMarkerIds.has(String(m.id))) return;
                        const p = $createParagraphNode();
                        p.append(
                            $createMarkerNode({
                                markerId: String(m.id),
                                title: String(m.title || "Marker"),
                                timeSec: Number.isFinite(timeSec) ? timeSec : 0,
                            }),
                        );
                        root.append(p);
                        insertedMarkerIds.add(String(m.id));
                    };

                    const findMarkersBeforeWord = (w: V2TranscriptWord, segSourceIndex: number) =>
                        markersForRender.filter(
                            (m) => !insertedMarkerIds.has(m.id) && m.after && anchorMatchesWord(m.after, w, segSourceIndex),
                        );

                    const findMarkersAfterWord = (w: V2TranscriptWord, segSourceIndex: number) =>
                        markersForRender.filter(
                            (m) => !insertedMarkerIds.has(m.id) && m.before && anchorMatchesWord(m.before, w, segSourceIndex),
                        );

		                // If we have no visible words, still render empty scenes (e.g., an empty ScriptClip spine).
		                if (visibleWords.length === 0) {
		                    if (scenesForRender.length === 0) {
		                        renderEmptySceneLine({ sceneId: "scene:0", sceneIndex: 0, startSec: 0, endSec: 0 });
		                        return;
		                    }
		                    for (const _scene of scenesForRender as any[]) {
		                        const sceneStart = Number((_scene as any)?.startSec ?? 0);
		                        const sceneEnd = Number((_scene as any)?.endSec ?? sceneStart);
		                        renderEmptySceneLine({
		                            sceneId: String((_scene as any)?.id ?? ""),
		                            sceneIndex: Number((_scene as any)?.index ?? 0),
		                            startSec: Number.isFinite(sceneStart) ? sceneStart : 0,
		                            endSec: Number.isFinite(sceneEnd) ? sceneEnd : Number.isFinite(sceneStart) ? sceneStart : 0,
		                        });
		                    }
		                    return;
		                }

	                const scenes =
	                    scenesForRender.length > 0
	                        ? [...scenesForRender].sort((a: any, b: any) => (a.startSec ?? 0) - (b.startSec ?? 0))
	                        : [{ startSec: 0, endSec: visibleWords[visibleWords.length - 1]?.endSec ?? visibleWords[visibleWords.length - 1]?.startSec ?? 0 }];

	                let wordIdx = 0;
	                const WORD_EPS = 1e-3;

		                for (const scene of scenes as any[]) {
	                    const sceneStart = Number(scene?.startSec ?? 0);
	                    const sceneEnd = Number(scene?.endSec ?? sceneStart);
		                    if (!Number.isFinite(sceneStart) || !Number.isFinite(sceneEnd) || sceneEnd <= sceneStart) {
		                        continue;
		                    }

		                    const sceneBreakPayload = {
		                        sceneId: String(scene?.id ?? ""),
		                        sceneIndex: Number(scene?.index ?? 0),
		                        startSec: sceneStart,
		                        endSec: sceneEnd,
		                    };
		                    let pendingSceneBreak = sceneStart > WORD_EPS ? sceneBreakPayload : null;

	                    // Collect words that fall inside this scene.
	                    const sceneWords: (TrackedWord & { isStrikethrough?: boolean })[] = [];
	                    while (wordIdx < visibleWords.length) {
	                        const w = visibleWords[wordIdx];
	                        const t = Number(w?.startSec ?? 0);
	                        if (!Number.isFinite(t)) {
	                            wordIdx++;
	                            continue;
	                        }
	                        if (t < sceneStart - WORD_EPS) {
	                            wordIdx++;
	                            continue;
	                        }
	                        if (t >= sceneEnd - WORD_EPS) break;
	                        sceneWords.push(w);
	                        wordIdx++;
	                    }

		                    if (sceneWords.length === 0) {
		                        // Always render empty scenes as a standalone "/" line.
		                        renderEmptySceneLine(sceneBreakPayload);
		                        continue;
		                    }

	                    const speakerSegmentsForScene: (SpeakerSegment & { isPending?: boolean })[] = [];
	                    let currentSegment: (SpeakerSegment & { isPending?: boolean }) | null = null;

	                    for (const w of sceneWords) {
	                        const effective = getEffectiveSpeaker(w);

	                        if (!currentSegment || currentSegment.speakerKey !== effective.speakerKey) {
	                            currentSegment = {
	                                speakerKey: effective.speakerKey,
	                                sourceIndex: w.sourceIndex,
	                                speakerId: w.speakerId,
	                                speaker: effective.speaker,
	                                sourceName: w.sourceName,
	                                sourceColor: effective.color,
	                                words: [],
	                                overrideId: effective.overrideId,
	                                isPending: effective.isPending,
	                                firestoreSpeakerId: effective.firestoreSpeakerId,
	                            };
	                            speakerSegmentsForScene.push(currentSegment);
	                        }
	                        currentSegment.words.push(w);
	                    }

	                    // Step 2: For each speaker segment (within this scene), apply paragraphing and render.
	                    for (const segment of speakerSegmentsForScene) {
	                        const isHiddenSpeaker = segment.speaker === '__hidden__';

	                        const segmentStartSec =
	                            segment.words.length > 0 ? Math.min(...segment.words.map(w => w.startSec ?? 0)) : 0;
	                        const segmentEndSec =
	                            segment.words.length > 0 ? Math.max(...segment.words.map(w => w.endSec ?? w.startSec ?? 0)) : 0;

                            // If a marker is anchored at the start of this segment, insert it BEFORE the speaker label
                            // so it acts as a true header for the content below.
                            const firstWordInSegment = segment.words?.[0] as any;
                            if (firstWordInSegment) {
                                const markersBeforeSegment = findMarkersBeforeWord(firstWordInSegment, segment.sourceIndex);
                                for (const m of markersBeforeSegment) {
                                    insertMarker(m, lastRenderedWordEndSec ?? 0);
                                }
                            }

			                        if (!isHiddenSpeaker) {
			                            const labelParagraph = $createParagraphNode();
			                            if (pendingSceneBreak) {
			                                labelParagraph.append($createSceneBreakNode(pendingSceneBreak));
			                                labelParagraph.append($createTextNode(" "));
			                                pendingSceneBreak = null;
			                            }
			                            if (scriptClipId) {
                                        const isMutedSpeaker = ScriptLayerStore.isSpeakerMutedAtSegment(
                                            {
                                                sourceIndex: segment.sourceIndex,
                                                diarizationSpeakerId:
                                                    segment.speakerId != null ? String(segment.speakerId) : undefined,
                                                speakerId: segment.firestoreSpeakerId,
                                                speakerName: segment.speaker,
                                            },
                                            segmentStartSec,
                                            segmentEndSec,
                                        );
			                                const speakerNode = $createSpeakerNode({
			                                    sourceIndex: segment.sourceIndex,
		                                    /* @ts-ignore */
	                                    speakerId: segment.speakerId,
                                        firestoreSpeakerId: segment.firestoreSpeakerId,
	                                    displayName: segment.isPending ? '' : segment.speaker,
	                                    color: segment.sourceColor,
	                                    clipId: scriptClipId,
	                                    overrideId: segment.overrideId,
	                                    segmentStartSec,
	                                    segmentEndSec,
                                        isMuted: isMutedSpeaker,
	                                });
	                                labelParagraph.append(speakerNode);
	                            }
	                            root.append(labelParagraph);
	                        }

	                        const segmentWords: V2TranscriptWord[] = segment.words.map(w => ({
	                            text: w.text,
                                kind: w.kind,
	                            startSec: w.startSec,
	                            endSec: w.endSec,
	                            speaker: w.speaker,
	                            srcStartSec: w.srcStartSec,
	                            srcEndSec: w.srcEndSec,
	                        }));

	                        const sentences = wordsToSentences(segmentWords);
	                        const paragraphs = sentencesToParagraphs(sentences);

                            const paragraphBreaks = Array.isArray(ScriptLayerStore.transcriptParagraphBreaks)
                                ? (ScriptLayerStore.transcriptParagraphBreaks as any[])
                                : [];
                            const shouldBreakAfterWord = (w: V2TranscriptWord): boolean => {
                                const wSrcStart = Number.isFinite(w.srcStartSec) ? Number(w.srcStartSec) : undefined;
                                const wSrcEnd = Number.isFinite(w.srcEndSec) ? Number(w.srcEndSec) : undefined;
                                const wClipEnd = Number.isFinite(w.endSec) ? Number(w.endSec) : Number.isFinite(w.startSec) ? Number(w.startSec) : undefined;
                                const EPS = 1e-3;

                                for (const b of paragraphBreaks) {
                                    if (!b) continue;
                                    const bSourceIndex = Number.isFinite((b as any)?.sourceIndex) ? Number((b as any).sourceIndex) : undefined;
                                    if (bSourceIndex != null && bSourceIndex !== segment.sourceIndex) continue;

                                    const bSrcStart = Number.isFinite((b as any)?.srcStartSec) ? Number((b as any).srcStartSec) : undefined;
                                    const bSrcEnd = Number.isFinite((b as any)?.srcEndSec) ? Number((b as any).srcEndSec) : undefined;
                                    const bTime = Number.isFinite((b as any)?.timeSec) ? Number((b as any).timeSec) : undefined;

                                    const hasStableKey = bSrcStart != null || bSrcEnd != null || bTime != null;
                                    if (!hasStableKey) continue;

                                    if (bSrcStart != null) {
                                        if (wSrcStart == null || Math.abs(wSrcStart - bSrcStart) > EPS) continue;
                                    }
                                    if (bSrcEnd != null) {
                                        if (wSrcEnd == null || Math.abs(wSrcEnd - bSrcEnd) > EPS) continue;
                                    }
                                    if (bTime != null) {
                                        if (wClipEnd == null || Math.abs(wClipEnd - bTime) > EPS) continue;
                                    }

                                    return true;
                                }

                                return false;
                            };

	                        for (const para of paragraphs) {
	                            const paraWords: V2TranscriptWord[] = [];
	                            for (const sentence of para.sentences) {
	                                paraWords.push(...sentence.words);
	                            }

                                let pNode = $createParagraphNode();
                                let hasWordsInNode = false;

	                            for (let idx = 0; idx < paraWords.length; idx++) {
                                    const w = paraWords[idx];

                                    // Insert any markers that are anchored BEFORE this word.
                                    const markersBeforeWord = findMarkersBeforeWord(w, segment.sourceIndex);
	                                    if (markersBeforeWord.length) {
	                                        if (hasWordsInNode) {
	                                            root.append(pNode);
	                                        }
	                                        for (const m of markersBeforeWord) {
	                                            insertMarker(m, lastRenderedWordEndSec ?? 0);
	                                        }
	                                        pNode = $createParagraphNode();
	                                        hasWordsInNode = false;
	                                    }

	                                    if (pendingSceneBreak && !hasWordsInNode) {
	                                        pNode.append($createSceneBreakNode(pendingSceneBreak));
	                                        pNode.append($createTextNode(" "));
	                                        pendingSceneBreak = null;
	                                    }

		                                const node = new WordNode(w.text, {
		                                    clipId: scriptClipId || undefined,
                                        kind: w.kind,
		                                    startSec: w.startSec,
		                                    endSec: w.endSec,
	                                    sourceIndex: segment.sourceIndex,
	                                    speaker: segment.speaker,
	                                    baseStartSec: para.startSec,
	                                    srcStartSec: w.srcStartSec,
	                                    srcEndSec: w.srcEndSec,
	                                });

	                                const wordTime = w.startSec ?? 0;
                                    const isAudioEventToken = String(w.kind ?? "word") === "audio_event";
	                                const { isFillerWord: checkFillerWord } = require('../FillerWords/fillerWord');
	                                const isFiller = checkFillerWord(w.text);
                                    const styleParts: string[] = [];
                                    if (isAudioEventToken) {
                                        styleParts.push("font-style: italic; color: var(--app-text-muted); opacity: 0.92;");
                                    }

	                                if (ScriptLayerStore.isTimeInRetakeCut(wordTime)) {
                                        styleParts.push("text-decoration: line-through; text-decoration-color: #DC2626; text-decoration-thickness: 2px;");
	                                } else if (ScriptLayerStore.isTimeInStrikethroughCut(wordTime)) {
                                        styleParts.push("text-decoration: line-through;");
	                                } else if (FillerWordStore.isActive && FillerWordStore.isCurrentOccurrence(wordTime)) {
                                        styleParts.push("background-color: #FED7AA; border-radius: 2px; padding: 1px 2px;");
	                                } else if (FillerWordStore.isActive && FillerWordStore.getOccurrenceAtTime(wordTime)) {
                                        styleParts.push("background-color: #FEF3C7; border-radius: 2px; padding: 1px 2px;");
	                                } else if (!isAudioEventToken && isFiller) {
                                        styleParts.push("text-decoration: underline dotted; text-decoration-color: #9ca3af; text-underline-offset: 3px;");
	                                }

                                    if (styleParts.length > 0) {
                                        node.setStyle(styleParts.join(" "));
                                    }

	                                pNode.append(node);
                                    hasWordsInNode = true;
                                    lastRenderedWordEndSec = Number.isFinite(w.endSec)
                                        ? Number(w.endSec)
                                        : Number.isFinite(w.startSec)
                                          ? Number(w.startSec)
                                          : lastRenderedWordEndSec;

                                    const nextWord = paraWords[idx + 1];
                                    const markersAfterWord = findMarkersAfterWord(w, segment.sourceIndex);
                                    const breakAfter = shouldBreakAfterWord(w) || markersAfterWord.length > 0;

	                                if (nextWord && !breakAfter) {
	                                    const needsSpace = !!w.text && !/^[.,!?;:)\]]$/.test(w.text);
	                                    if (needsSpace) {
	                                        pNode.append($createTextNode(" "));
	                                    }
	                                }

                                    if (markersAfterWord.length) {
                                        // Marker forces a paragraph break AND inserts a header.
                                        root.append(pNode);
                                        for (const m of markersAfterWord) {
                                            insertMarker(m, lastRenderedWordEndSec ?? 0);
                                        }
                                        pNode = $createParagraphNode();
                                        hasWordsInNode = false;
                                    } else if (breakAfter && nextWord) {
                                        root.append(pNode);
                                        pNode = $createParagraphNode();
                                        hasWordsInNode = false;
                                    }
	                            }

                                if (hasWordsInNode) {
                                    root.append(pNode);
                                }
	                        }
	                    }
		                }
		                
		                // Restore caret position after rebuild to avoid scroll-to-top jumps.
		                // We only do this when the editor was focused when the rebuild started.
		                if (isEditorFocused && selectionBoundary) {
		                    try {
		                        const EPS = 1e-3;
		                        const matches = (payload: any, anchor: any): boolean => {
		                            if (!payload || !anchor) return false;
		                            const aSourceIndex = Number.isFinite(anchor?.sourceIndex) ? Number(anchor.sourceIndex) : undefined;
		                            const aSrcStart = Number.isFinite(anchor?.srcStartSec) ? Number(anchor.srcStartSec) : undefined;
		                            const aSrcEnd = Number.isFinite(anchor?.srcEndSec) ? Number(anchor.srcEndSec) : undefined;
		                            const aStart = Number.isFinite(anchor?.startSec) ? Number(anchor.startSec) : undefined;
		                            const aEnd = Number.isFinite(anchor?.endSec) ? Number(anchor.endSec) : undefined;

		                            const pSourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
		                            if (aSourceIndex != null && pSourceIndex != null && aSourceIndex !== pSourceIndex) return false;

		                            const pSrcStart = Number.isFinite(payload?.srcStartSec) ? Number(payload.srcStartSec) : undefined;
		                            const pSrcEnd = Number.isFinite(payload?.srcEndSec) ? Number(payload.srcEndSec) : undefined;
		                            const pStart = Number.isFinite(payload?.startSec) ? Number(payload.startSec) : undefined;
		                            const pEnd = Number.isFinite(payload?.endSec)
		                                ? Number(payload.endSec)
		                                : Number.isFinite(payload?.startSec)
		                                  ? Number(payload.startSec)
		                                  : undefined;

		                            const hasStable = aSrcStart != null || aSrcEnd != null || aStart != null || aEnd != null;
		                            if (!hasStable) return false;

		                            if (aSrcStart != null) {
		                                if (pSrcStart == null || Math.abs(pSrcStart - aSrcStart) > EPS) return false;
		                            }
		                            if (aSrcEnd != null) {
		                                if (pSrcEnd == null || Math.abs(pSrcEnd - aSrcEnd) > EPS) return false;
		                            }
		                            if (aStart != null) {
		                                if (pStart == null || Math.abs(pStart - aStart) > EPS) return false;
		                            }
		                            if (aEnd != null) {
		                                if (pEnd == null || Math.abs(pEnd - aEnd) > EPS) return false;
		                            }
		                            return true;
		                        };

		                        const wordNodes = root.getAllTextNodes().filter((n) => n instanceof WordNode) as WordNode[];
		                        const afterAnchor = selectionBoundary.after;
		                        const beforeAnchor = selectionBoundary.before;

		                        const afterNode =
		                            afterAnchor != null
		                                ? wordNodes.find((n) => matches((n as any).__payload, afterAnchor))
		                                : null;
		                        const beforeNode =
		                            !afterNode && beforeAnchor != null
		                                ? wordNodes.find((n) => matches((n as any).__payload, beforeAnchor))
		                                : null;

		                        if (afterNode) {
		                            const sel = $createRangeSelection();
		                            sel.anchor.set(afterNode.getKey(), 0, "text");
		                            sel.focus.set(afterNode.getKey(), 0, "text");
		                            $setSelection(sel);
		                        } else if (beforeNode) {
		                            const size = beforeNode.getTextContentSize();
		                            const sel = $createRangeSelection();
		                            sel.anchor.set(beforeNode.getKey(), size, "text");
		                            sel.focus.set(beforeNode.getKey(), size, "text");
		                            $setSelection(sel);
		                        }
		                    } catch {}
		                }
	            });
	            
	            // Restore scroll position after DOM update
	            requestAnimationFrame(() => {
	                if (scrollContainer) {
	                    scrollContainer.scrollTop = scrollTop;
	                    requestAnimationFrame(() => {
	                        if (scrollContainer) {
	                            scrollContainer.scrollTop = scrollTop;
	                        }
	                    });
	                }
	            });
	        })();
	        return () => {
	            cancelled = true;
        };
    // Also observe ScriptLayerStore.items.length, cutsVersion, scenesVersion, filler word state, and speaker overrides to trigger updates
    }, [editor, words, GetTranscript, GetTranscriptChunk, CompositionStateStore.updateTimestamp, itemsLength, cutsVersion, scenesVersion, transcriptHiddenRangesVersion, wordCorrectionsVersion, transcriptParagraphBreaksVersion, transcriptMarkersVersion, fillerWordActive, fillerWordCurrentIndex, fillerWordOccurrencesCount, speakerOverridesVersion, speakerMuteRangesVersion, pendingSpeakerOverride, projectTracksVersion, showWordlessAudioEventsOnScript, transcriptDocTick]);
    
    return null;
});

/**
 * SceneSplitPlugin: Handles "/" keypress for scene operations.
 *
 * Rules:
 * - "/" BETWEEN words => split the current scene (adds a scene-boundary split; no time is added).
 * - "/" on an EMPTY line => only inserts an empty scene if the caret is ALREADY between scenes
 *   (i.e. there is an existing scene boundary between the surrounding words). Otherwise, it
 *   behaves like a split (split first).
 */
const SceneSplitPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();
    
    useEffect(() => {
        return editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (event.key !== '/') return false;
                
                // Prevent the "/" from being typed
                event.preventDefault();
                
                // Get current selection to find cursor position
                const selection = $getSelection();
                if (!selection || !$isRangeSelection(selection)) return true;
                
                // Find the word node at or before the cursor
                const anchor = selection.anchor;
                const anchorNode = anchor.getNode();

                // Determine whether the caret is on an "empty line" (empty paragraph).
                let paragraphNode: any = anchorNode;
                while (paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() !== "paragraph") {
                    paragraphNode = paragraphNode.getParent?.();
                }
                const isEmptyParagraph =
                    paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() === "paragraph"
                        ? (() => {
                              const text = String(paragraphNode.getTextContent?.() ?? "").trim();
                              return text.length === 0 || text === "/";
                          })()
                        : false;

                const findFirstWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const first = node.getFirstChild?.();
                    if (!first) return null;
                    let child: any = first;
                    while (child) {
                        const found = findFirstWord(child);
                        if (found) return found;
                        child = child.getNextSibling?.();
                    }
                    return null;
                };

                const findLastWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const last = node.getLastChild?.();
                    if (!last) return null;
                    let child: any = last;
                    while (child) {
                        const found = findLastWord(child);
                        if (found) return found;
                        child = child.getPreviousSibling?.();
                    }
                    return null;
                };
                
                // Find word nodes around the cursor
                let prevWord: WordNode | null = null;
                let nextWord: WordNode | null = null;
                
                if ($isWordNode(anchorNode)) {
                    // Cursor is on a word.
                    // If the caret is inside the word (or at its start), treat it as BEFORE the word.
                    // Only treat it as AFTER the word when the caret is at the end.
                    const wordNode = anchorNode as WordNode;
                    const size = wordNode.getTextContentSize();
                    const atEnd = anchor.offset >= size;

                    if (atEnd) {
                        prevWord = wordNode;
                        let sibling = wordNode.getNextSibling();
                        while (sibling) {
                            if ($isWordNode(sibling)) {
                                nextWord = sibling;
                                break;
                            }
                            sibling = sibling.getNextSibling();
                        }
                    } else {
                        nextWord = wordNode;
                        let sibling = wordNode.getPreviousSibling();
                        while (sibling) {
                            if ($isWordNode(sibling)) {
                                prevWord = sibling;
                                break;
                            }
                            sibling = sibling.getPreviousSibling();
                        }
                    }
                } else {
                    // Cursor might be in a text node (space) or an empty paragraph - find surrounding words.
                    let sibling: any = anchorNode.getPreviousSibling?.();
                    while (sibling) {
                        if ($isWordNode(sibling)) {
                            prevWord = sibling;
                            break;
                        }
                        sibling = sibling.getPreviousSibling?.();
                    }
                    sibling = anchorNode.getNextSibling?.();
                    while (sibling) {
                        if ($isWordNode(sibling)) {
                            nextWord = sibling;
                            break;
                        }
                        sibling = sibling.getNextSibling?.();
                    }
                }

                // If we didn't find words within the same paragraph, search across paragraphs.
                if (!prevWord) {
                    let p: any = paragraphNode?.getPreviousSibling?.();
                    while (p) {
                        const last = findLastWord(p);
                        if (last) {
                            prevWord = last;
                            break;
                        }
                        p = p.getPreviousSibling?.();
                    }
                }
                if (!nextWord) {
                    let p: any = paragraphNode?.getNextSibling?.();
                    while (p) {
                        const first = findFirstWord(p);
                        if (first) {
                            nextWord = first;
                            break;
                        }
                        p = p.getNextSibling?.();
                    }
                }

                // No surrounding words => nothing to do.
                if (!prevWord && !nextWord) return true;
                
                // Calculate split time: midpoint between prev word end and next word start
                // If no next word, use just after prev word end
                const prevEndSec = prevWord?.__payload.endSec ?? prevWord?.__payload.startSec ?? 0;
                const nextStartSec = nextWord?.__payload.startSec ?? prevEndSec + 0.1;
                const splitTime = (prevEndSec + nextStartSec) / 2;

                // Empty scenes may only be inserted BETWEEN existing scenes (not between words).
                // So on an empty paragraph, only insert empty time if we're already between scenes.
                if (isEmptyParagraph && prevWord && nextWord) {
                    const existingBoundary = ScriptLayerStore.getSplitBetween(prevEndSec, nextStartSec);
                    if (existingBoundary != null && Number.isFinite(existingBoundary)) {
                        CompositionService.insertEmptyScriptScene(existingBoundary, 5);
                        return true;
                    }
                }

                // Default behavior: split scene boundary at this location (no time is added).
                CompositionService.splitScriptLayer(splitTime, true);
                
                return true;
            },
            COMMAND_PRIORITY_HIGH
        );
    }, [editor]);
    
    return null;
};

/**
 * MarkerInsertPlugin: Handles "#" keypress to insert a transcript marker.
 *
 * Rules:
 * - Marker is inserted at the START of the current selection (or caret).
 * - Marker anchors between words (before the "next" word at the insertion point).
 * - Marker title defaults to "Marker" and can be renamed via double-click.
 */
const MarkerInsertPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (event.key !== "#") return false;

                const target = event.target as HTMLElement | null;
                if (!target || !target.closest(".v2editor-scroll")) return false;

                event.preventDefault();

                const selection = $getSelection();
                if (!selection || !$isRangeSelection(selection)) return true;

                const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
                const startNode = startPoint.getNode();

                // Find containing paragraph for cross-paragraph searches.
                let paragraphNode: any = startNode;
                while (paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() !== "paragraph") {
                    paragraphNode = paragraphNode.getParent?.();
                }

                const findFirstWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const first = node.getFirstChild?.();
                    if (!first) return null;
                    let child: any = first;
                    while (child) {
                        const found = findFirstWord(child);
                        if (found) return found;
                        child = child.getNextSibling?.();
                    }
                    return null;
                };

                const findLastWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const last = node.getLastChild?.();
                    if (!last) return null;
                    let child: any = last;
                    while (child) {
                        const found = findLastWord(child);
                        if (found) return found;
                        child = child.getPreviousSibling?.();
                    }
                    return null;
                };

                const findPrevWordInDoc = (node: any): WordNode | null => {
                    let sibling: any = node?.getPreviousSibling?.();
                    while (sibling) {
                        const found = findLastWord(sibling);
                        if (found) return found;
                        sibling = sibling.getPreviousSibling?.();
                    }
                    let p: any = paragraphNode?.getPreviousSibling?.();
                    while (p) {
                        const found = findLastWord(p);
                        if (found) return found;
                        p = p.getPreviousSibling?.();
                    }
                    return null;
                };

                const findNextWordInDoc = (node: any): WordNode | null => {
                    let sibling: any = node?.getNextSibling?.();
                    while (sibling) {
                        const found = findFirstWord(sibling);
                        if (found) return found;
                        sibling = sibling.getNextSibling?.();
                    }
                    let p: any = paragraphNode?.getNextSibling?.();
                    while (p) {
                        const found = findFirstWord(p);
                        if (found) return found;
                        p = p.getNextSibling?.();
                    }
                    return null;
                };

                let prevWord: WordNode | null = null;
                let nextWord: WordNode | null = null;

                if ($isWordNode(startNode)) {
                    const wordNode = startNode as WordNode;
                    const size = wordNode.getTextContentSize();
                    const atEnd = startPoint.offset >= size;

                    if (atEnd) {
                        prevWord = wordNode;
                        nextWord = findNextWordInDoc(wordNode);
                    } else {
                        nextWord = wordNode;
                        prevWord = findPrevWordInDoc(wordNode);
                    }
                } else {
                    prevWord = findPrevWordInDoc(startNode);
                    nextWord = findNextWordInDoc(startNode);
                }

                if (!prevWord && !nextWord) return true;

                CompositionService.addTranscriptMarker({
                    title: "Marker",
                    afterWordPayload: nextWord ? nextWord.__payload : undefined,
                    beforeWordPayload: prevWord ? prevWord.__payload : undefined,
                });

                return true;
            },
            COMMAND_PRIORITY_HIGH,
        );
    }, [editor]);

    return null;
};

/**
 * ScriptChunkClipboardPlugin:
 * - Cmd/Ctrl+C: copy the selected transcript range into an internal "script chunk" clipboard
 *   (also allowing normal text copy to proceed).
 * - Cmd/Ctrl+V: paste the copied script chunk at the current caret boundary (between words).
 *
 * This inserts fillable spine time on the ScriptLayerClip and appends source segments,
 * preserving per-scene layout/visual properties for the copied section.
 */
const ScriptChunkClipboardPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                const target = event.target as HTMLElement | null;
                if (!target || !target.closest(".v2editor-scroll")) return false;
                const tag = String((target as any)?.tagName ?? "").toLowerCase();
                if (tag === "input" || tag === "textarea") return false;

                const isCopy = (event.metaKey || event.ctrlKey) && !event.altKey && String(event.key ?? "").toLowerCase() === "c";
                const isPaste = (event.metaKey || event.ctrlKey) && !event.altKey && String(event.key ?? "").toLowerCase() === "v";

                if (!isCopy && !isPaste) return false;

                if (isCopy) {
                    // Best-effort: also store script chunk while allowing normal browser copy.
                    editor.getEditorState().read(() => {
                        const selection = $getSelection();
                        if (!selection || !$isRangeSelection(selection) || selection.isCollapsed()) return;
                        const nodes = selection.getNodes().filter((n) => $isWordNode(n)) as WordNode[];
                        if (nodes.length === 0) return;

                        const starts = nodes.map((n) => Number(n.__payload.startSec ?? NaN)).filter(Number.isFinite);
                        if (starts.length === 0) return;
                        const ends = nodes
                            .map((n) =>
                                Number.isFinite(Number(n.__payload.endSec))
                                    ? Number(n.__payload.endSec)
                                    : Number.isFinite(Number(n.__payload.startSec))
                                      ? Number(n.__payload.startSec)
                                      : NaN,
                            )
                            .filter(Number.isFinite);
                        if (ends.length === 0) return;

                        const startSec = Math.min(...starts);
                        const endSec = Math.max(...ends);
                        const chunk = CompositionService.copyScriptChunk(startSec, endSec);
                        if (chunk) {
                            ApplicationStore.setScriptChunkClipboard(chunk);
                        }
                    });
                    return false;
                }

                // Paste: prevent text paste and insert the chunk into the script spine.
                const chunk = ApplicationStore.getScriptChunkClipboard();
                if (!chunk) return false;

                editor.getEditorState().read(() => {
                    const selection = $getSelection();
                    if (!selection || !$isRangeSelection(selection) || !selection.isCollapsed()) return;

                    const point = selection.anchor;
                    const startNode = point.getNode();

                    // Find containing paragraph for cross-paragraph searches.
                    let paragraphNode: any = startNode;
                    while (paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() !== "paragraph") {
                        paragraphNode = paragraphNode.getParent?.();
                    }

                    const findFirstWord = (node: any): WordNode | null => {
                        if (!node) return null;
                        if ($isWordNode(node)) return node as WordNode;
                        const first = node.getFirstChild?.();
                        if (!first) return null;
                        let child: any = first;
                        while (child) {
                            const found = findFirstWord(child);
                            if (found) return found;
                            child = child.getNextSibling?.();
                        }
                        return null;
                    };

                    const findLastWord = (node: any): WordNode | null => {
                        if (!node) return null;
                        if ($isWordNode(node)) return node as WordNode;
                        const last = node.getLastChild?.();
                        if (!last) return null;
                        let child: any = last;
                        while (child) {
                            const found = findLastWord(child);
                            if (found) return found;
                            child = child.getPreviousSibling?.();
                        }
                        return null;
                    };

                    const findPrevWordInDoc = (node: any): WordNode | null => {
                        let sibling: any = node?.getPreviousSibling?.();
                        while (sibling) {
                            const found = findLastWord(sibling);
                            if (found) return found;
                            sibling = sibling.getPreviousSibling?.();
                        }
                        let p: any = paragraphNode?.getPreviousSibling?.();
                        while (p) {
                            const found = findLastWord(p);
                            if (found) return found;
                            p = p.getPreviousSibling?.();
                        }
                        return null;
                    };

                    const findNextWordInDoc = (node: any): WordNode | null => {
                        let sibling: any = node?.getNextSibling?.();
                        while (sibling) {
                            const found = findFirstWord(sibling);
                            if (found) return found;
                            sibling = sibling.getNextSibling?.();
                        }
                        let p: any = paragraphNode?.getNextSibling?.();
                        while (p) {
                            const found = findFirstWord(p);
                            if (found) return found;
                            p = p.getNextSibling?.();
                        }
                        return null;
                    };

                    let prevWord: WordNode | null = null;
                    let nextWord: WordNode | null = null;

                    if ($isWordNode(startNode)) {
                        const wordNode = startNode as WordNode;
                        const size = wordNode.getTextContentSize();
                        const atEnd = point.offset >= size;
                        if (atEnd) {
                            prevWord = wordNode;
                            nextWord = findNextWordInDoc(wordNode);
                        } else {
                            nextWord = wordNode;
                            prevWord = findPrevWordInDoc(wordNode);
                        }
                    } else {
                        prevWord = findPrevWordInDoc(startNode);
                        nextWord = findNextWordInDoc(startNode);
                    }

                    const atClipLocalSec = Number(
                        prevWord?.__payload?.endSec ?? prevWord?.__payload?.startSec ?? (nextWord?.__payload?.startSec ?? 0),
                    );
                    if (!Number.isFinite(atClipLocalSec) || atClipLocalSec < 0) return;

                    event.preventDefault();
                    event.stopPropagation();
                    void CompositionService.pasteScriptChunk(atClipLocalSec, chunk).catch((e) => {
                        console.warn("[ScriptChunkClipboardPlugin] paste failed", e);
                    });
                });

                return true;
            },
            COMMAND_PRIORITY_HIGH,
        );
    }, [editor]);

    return null;
};

/**
 * EmptyLineActionsOverlay: Shows inline actions when the caret is on an empty line:
 * - Split scene (equivalent to typing "/")
 * - Add marker (equivalent to typing "#")
 */
const EmptyLineActionsOverlay: React.FC<{
    containerRef: React.RefObject<HTMLElement>;
}> = ({ containerRef }) => {
    const [editor] = useLexicalComposerContext();
    const [actions, setActions] = useState<{
        paragraphKey: string;
        anchorPos: { top: number; left: number };
        prevWordPayload?: any;
        nextWordPayload?: any;
        splitTimeSec: number;
	    } | null>(null);

	    const actionsRef = useRef<typeof actions>(null);
	    useEffect(() => {
	        actionsRef.current = actions;
	    }, [actions]);

	    const overlayRef = useRef<HTMLDivElement | null>(null);
	    const [overlayWidthPx, setOverlayWidthPx] = useState<number>(0);
	    useLayoutEffect(() => {
	        if (!actions) return;
	        const el = overlayRef.current;
	        if (!el) return;

	        const measure = () => {
	            try {
	                const w = el.getBoundingClientRect().width;
	                setOverlayWidthPx(Number.isFinite(w) ? w : 0);
	            } catch {
	                setOverlayWidthPx(0);
	            }
	        };
	        measure();

	        if (typeof ResizeObserver === "undefined") return;
	        const ro = new ResizeObserver(() => measure());
	        ro.observe(el);
	        return () => ro.disconnect();
	    }, [actions?.paragraphKey]);

	    const updatePosition = () => {
	        const current = actionsRef.current;
	        if (!current) return;
	        try {
            const el = editor.getElementByKey?.(current.paragraphKey) as HTMLElement | null;
            const containerRect = containerRef.current?.getBoundingClientRect?.() ?? null;
            if (!el || !containerRect) return;
	            const rect = el.getBoundingClientRect();
	            const nextPos = {
	                top: rect.top - containerRect.top + rect.height / 2,
	                left: rect.left - containerRect.left,
	            };
	            setActions((prev) => {
	                if (!prev) return prev;
	                if (Math.abs(prev.anchorPos.top - nextPos.top) < 0.5 && Math.abs(prev.anchorPos.left - nextPos.left) < 0.5) return prev;
                return { ...prev, anchorPos: nextPos };
            });
        } catch {}
    };

    useEffect(() => {
        const scrollContainer = (editor.getRootElement?.() ?? document.querySelector(".v2editor-scroll")) as HTMLElement | null;
        if (!scrollContainer) return;
        const onScroll = () => updatePosition();
        scrollContainer.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            scrollContainer.removeEventListener("scroll", onScroll as any);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

	    useEffect(() => {
	        // Use an update listener (instead of SELECTION_CHANGE_COMMAND) so we read the latest committed
	        // editor state. This avoids the overlay lagging behind the caret on mouse clicks.
	        return editor.registerUpdateListener(({ editorState }) => {
	            let paragraphKey: string | null = null;
	            let splitTimeSec: number | null = null;
	            let prevWordPayload: any | undefined;
	            let nextWordPayload: any | undefined;
	            let shouldShow = false;

	            editorState.read(() => {
	                const selection = $getSelection();
	                if (!selection || !$isRangeSelection(selection) || !selection.isCollapsed()) return;

	                const anchorNode = selection.anchor.getNode();

	                // Find containing paragraph for cross-paragraph searches.
	                let paragraphNode: any = anchorNode;
	                while (
	                    paragraphNode &&
	                    typeof paragraphNode.getType === "function" &&
	                    paragraphNode.getType() !== "paragraph"
	                ) {
	                    paragraphNode = paragraphNode.getParent?.();
	                }
	                if (!paragraphNode || typeof paragraphNode.getType !== "function" || paragraphNode.getType() !== "paragraph") return;

	                // Only show on a truly "empty" line (no words) or the empty-scene placeholder line ("/").
	                let hasWord = false;
	                let child: any = paragraphNode.getFirstChild?.();
	                while (child) {
	                    if ($isWordNode(child)) {
	                        hasWord = true;
	                        break;
	                    }
	                    child = child.getNextSibling?.();
	                }
	                if (hasWord) return;

	                const text = String(paragraphNode.getTextContent?.() ?? "").trim();
	                if (text !== "" && text !== "/") return;

	                const findFirstWord = (node: any): WordNode | null => {
	                    if (!node) return null;
	                    if ($isWordNode(node)) return node as WordNode;
	                    const first = node.getFirstChild?.();
	                    if (!first) return null;
	                    let c: any = first;
	                    while (c) {
	                        const found = findFirstWord(c);
	                        if (found) return found;
	                        c = c.getNextSibling?.();
	                    }
	                    return null;
	                };

	                const findLastWord = (node: any): WordNode | null => {
	                    if (!node) return null;
	                    if ($isWordNode(node)) return node as WordNode;
	                    const last = node.getLastChild?.();
	                    if (!last) return null;
	                    let c: any = last;
	                    while (c) {
	                        const found = findLastWord(c);
	                        if (found) return found;
	                        c = c.getPreviousSibling?.();
	                    }
	                    return null;
	                };

	                const findPrevWordInDoc = (node: any): WordNode | null => {
	                    let sibling: any = node?.getPreviousSibling?.();
	                    while (sibling) {
	                        const found = findLastWord(sibling);
	                        if (found) return found;
	                        sibling = sibling.getPreviousSibling?.();
	                    }
	                    let p: any = paragraphNode?.getPreviousSibling?.();
	                    while (p) {
	                        const found = findLastWord(p);
	                        if (found) return found;
	                        p = p.getPreviousSibling?.();
	                    }
	                    return null;
	                };

	                const findNextWordInDoc = (node: any): WordNode | null => {
	                    let sibling: any = node?.getNextSibling?.();
	                    while (sibling) {
	                        const found = findFirstWord(sibling);
	                        if (found) return found;
	                        sibling = sibling.getNextSibling?.();
	                    }
	                    let p: any = paragraphNode?.getNextSibling?.();
	                    while (p) {
	                        const found = findFirstWord(p);
	                        if (found) return found;
	                        p = p.getNextSibling?.();
	                    }
	                    return null;
	                };

	                const prevWord = findPrevWordInDoc(anchorNode);
	                const nextWord = findNextWordInDoc(anchorNode);
	                if (!prevWord && !nextWord) return;

	                const prevEndSec = prevWord?.__payload?.endSec ?? prevWord?.__payload?.startSec ?? 0;
	                const nextStartSec = nextWord?.__payload?.startSec ?? prevEndSec + 0.1;
	                const splitTime = (prevEndSec + nextStartSec) / 2;

	                paragraphKey = paragraphNode.getKey?.() ?? null;
	                splitTimeSec = splitTime;
	                prevWordPayload = prevWord ? prevWord.__payload : undefined;
	                nextWordPayload = nextWord ? nextWord.__payload : undefined;
	                shouldShow = true;
	            });

	            if (!shouldShow || !paragraphKey || splitTimeSec == null || !Number.isFinite(splitTimeSec)) {
	                if (actionsRef.current) setActions(null);
	                return;
	            }

	            const el = editor.getElementByKey?.(paragraphKey) as HTMLElement | null;
	            const containerRect = containerRef.current?.getBoundingClientRect?.() ?? null;
	            if (!el || !containerRect) {
	                if (actionsRef.current) setActions(null);
	                return;
	            }

	            const rect = el.getBoundingClientRect();
	            const anchorPos = {
	                top: rect.top - containerRect.top + rect.height / 2,
	                left: rect.left - containerRect.left,
	            };

	            const prev = actionsRef.current;
	            const prevWordId = prev?.prevWordPayload?.wordId ?? null;
	            const nextWordId = prev?.nextWordPayload?.wordId ?? null;
	            const nextPrevWordId = prevWordPayload?.wordId ?? null;
	            const nextNextWordId = nextWordPayload?.wordId ?? null;
	            if (
	                prev &&
	                prev.paragraphKey === paragraphKey &&
	                Math.abs(Number(prev.splitTimeSec) - Number(splitTimeSec)) < 1e-6 &&
	                prevWordId === nextPrevWordId &&
	                nextWordId === nextNextWordId
	            ) {
	                return;
	            }

	            setActions({
	                paragraphKey,
	                anchorPos,
	                prevWordPayload,
	                nextWordPayload,
	                splitTimeSec: Number(splitTimeSec),
	            });
	        });
	    }, [editor, containerRef]);

    const handleSplitScene = () => {
        const ctx = actions;
        if (!ctx) return;
        const splitTimeSec = Number(ctx.splitTimeSec ?? 0);
        if (!Number.isFinite(splitTimeSec)) return;
        CompositionService.splitScriptLayer(splitTimeSec, true);
        setActions(null);
    };

	    const handleAddMarker = () => {
	        const ctx = actions;
	        if (!ctx) return;
	        CompositionService.addTranscriptMarker({
            title: "Marker",
            afterWordPayload: ctx.nextWordPayload,
            beforeWordPayload: ctx.prevWordPayload,
        });
	        setActions(null);
	    };

		    const handleAddMedia = () => {
		        const ctx = actions;
		        if (!ctx) return;
		        const atClipLocalSec = Number(ctx.splitTimeSec ?? 0);
		        if (!Number.isFinite(atClipLocalSec)) return;
		        try {
		            ApplicationStore.setPendingInsertIntoScriptContext({
		                atClipLocalSec,
		                preferExistingEmptyScene: true,
		            });
		        } catch {}
		        try {
		            window.dispatchEvent(
		                new CustomEvent("openProjectSidebarFiles", {
		                    detail: { source: "empty-line-actions" },
		                }),
		            );
		        } catch {}
		        setActions(null);
		    };

	    if (!actions) return null;

	    const gapPx = 14;
	    const anchorX = Number(actions.anchorPos.left ?? 0);
	    const containerW = Number(containerRef.current?.getBoundingClientRect?.().width ?? 0);
	    const measuredOverlayW = Number(overlayWidthPx ?? 0);
	    // Place actions to the right of the caret/line start, with a small gap so the caret stays visible.
	    let leftPx = anchorX + gapPx;
	    if (containerW > 0 && measuredOverlayW > 0) {
	        leftPx = Math.min(leftPx, containerW - measuredOverlayW - 8);
	    }
	    leftPx = Math.max(8, leftPx);

    return (
        <Box
            ref={overlayRef}
            sx={{
                position: "absolute",
                top: `${Math.max(8, Number(actions.anchorPos.top ?? 0))}px`,
                left: `${leftPx}px`,
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                padding: 0,
                border: "none",
                backgroundColor: "transparent",
                boxShadow: "none",
                zIndex: 1001,
                transform: "translateY(-50%)",
            }}
            onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
	        >
	            <Button
	                variant="text"
	                onClick={handleAddMedia}
	                startIcon={<AddIcon fontSize="small" />}
	                sx={{
	                    textTransform: "none",
	                    fontSize: "13px",
	                    fontWeight: 500,
	                    color: "var(--app-text-primary)",
	                    minHeight: 32,
	                    borderRadius: "8px",
	                    padding: "4px 10px",
	                    "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
	                    "& .MuiButton-startIcon": { marginRight: "6px" },
	                }}
	            >
	                Add media
	            </Button>
	            <IconButton
	                size="small"
	                onClick={handleSplitScene}
	                title="Split scene"
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                }}
            >
                <SceneIcon />
            </IconButton>
            <IconButton
                size="small"
                onClick={handleAddMarker}
                title="Add marker"
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    "&:hover": { backgroundColor: "var(--app-bg-subtle)" },
                }}
            >
                <BookmarkBorderOutlinedIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

/**
 * ParagraphBreakPlugin: Handles Enter keypress for transcript paragraph breaks.
 *
 * Rules:
 * - Enter with a collapsed caret between words => insert a manual paragraph break (transcript-only; no playback changes).
 * - Enter with a range selection => Delete Media for the selection AND insert a paragraph break at the selection start.
 *   This matches "replace selection with newline" behavior from normal text editors.
 */
const ParagraphBreakPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (event.key !== "Enter") return false;

                const target = event.target as HTMLElement | null;
                if (!target || !target.closest(".v2editor-scroll")) return false;

                event.preventDefault();

                const selection = $getSelection();
                if (!selection || !$isRangeSelection(selection)) return true;

                const startPoint = selection.isBackward() ? selection.focus : selection.anchor;
                const startNode = startPoint.getNode();

                // Find containing paragraph for cross-paragraph searches.
                let paragraphNode: any = startNode;
                while (paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() !== "paragraph") {
                    paragraphNode = paragraphNode.getParent?.();
                }

                const findFirstWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const first = node.getFirstChild?.();
                    if (!first) return null;
                    let child: any = first;
                    while (child) {
                        const found = findFirstWord(child);
                        if (found) return found;
                        child = child.getNextSibling?.();
                    }
                    return null;
                };

                const findLastWord = (node: any): WordNode | null => {
                    if (!node) return null;
                    if ($isWordNode(node)) return node as WordNode;
                    const last = node.getLastChild?.();
                    if (!last) return null;
                    let child: any = last;
                    while (child) {
                        const found = findLastWord(child);
                        if (found) return found;
                        child = child.getPreviousSibling?.();
                    }
                    return null;
                };

                const findPrevWordInDoc = (node: any): WordNode | null => {
                    let sibling: any = node?.getPreviousSibling?.();
                    while (sibling) {
                        const found = findLastWord(sibling);
                        if (found) return found;
                        sibling = sibling.getPreviousSibling?.();
                    }
                    let p: any = paragraphNode?.getPreviousSibling?.();
                    while (p) {
                        const found = findLastWord(p);
                        if (found) return found;
                        p = p.getPreviousSibling?.();
                    }
                    return null;
                };

                const findNextWordInDoc = (node: any): WordNode | null => {
                    let sibling: any = node?.getNextSibling?.();
                    while (sibling) {
                        const found = findFirstWord(sibling);
                        if (found) return found;
                        sibling = sibling.getNextSibling?.();
                    }
                    let p: any = paragraphNode?.getNextSibling?.();
                    while (p) {
                        const found = findFirstWord(p);
                        if (found) return found;
                        p = p.getNextSibling?.();
                    }
                    return null;
                };

                // Determine the "previous word" for the insertion point (the word just before the break).
                let prevWord: WordNode | null = null;
                let nextWord: WordNode | null = null;

                if ($isWordNode(startNode)) {
                    const wordNode = startNode as WordNode;
                    const size = wordNode.getTextContentSize();
                    const atEnd = startPoint.offset >= size;

                    if (atEnd) {
                        prevWord = wordNode;
                        nextWord = findNextWordInDoc(wordNode);
                    } else {
                        nextWord = wordNode;
                        prevWord = findPrevWordInDoc(wordNode);
                    }
                } else {
                    // Cursor might be on a space node; use surrounding siblings / paragraphs.
                    prevWord = findPrevWordInDoc(startNode);
                    nextWord = findNextWordInDoc(startNode);
                }

                // If this is a range selection, treat it like "Delete Media" then insert paragraph break.
                if (!selection.isCollapsed()) {
                    let minStart = 0;
                    let maxEnd = 0;
                    let nodeKeys: string[] = [];

                    const nodes = selection.getNodes().filter((n) => $isWordNode(n)) as WordNode[];
                    if (!nodes.length) return true;

                    const nodesSorted = [...nodes].sort((a, b) => (Number(a.__payload.startSec ?? 0) - Number(b.__payload.startSec ?? 0)));
                    const firstSelectedWord = nodesSorted[0];
                    const lastSelectedWord = nodesSorted[nodesSorted.length - 1];

                    const prevWordForBreak = firstSelectedWord ? findPrevWordInDoc(firstSelectedWord) : null;
                    const nextWordAfterSelection = lastSelectedWord ? findNextWordInDoc(lastSelectedWord) : null;
                    const prevWordForBreakKey = prevWordForBreak ? prevWordForBreak.getKey() : null;
                    const shouldInsertParagraphBreak = Boolean(prevWordForBreakKey && nextWordAfterSelection);

                    const starts = nodes.map((n) => n.__payload.startSec ?? 0);
                    const ends = nodes.map((n) => n.__payload.endSec ?? n.__payload.startSec ?? 0);
                    minStart = Math.min(...starts);
                    maxEnd = Math.max(...ends);
                    nodeKeys = nodes.map((n) => n.getKey());

                    // Optimistically update transcript view immediately.
                    editor.update(
                        () => {
                            const removeWords = () => {
                                for (const key of nodeKeys) {
                                    const node = $getNodeByKey(key);
                                    if (!$isWordNode(node)) continue;

                                    const next = node.getNextSibling();
                                    if (next instanceof TextNode && next.getTextContent() === " ") {
                                        next.remove();
                                    } else {
                                        const prev = node.getPreviousSibling();
                                        if (prev instanceof TextNode && prev.getTextContent() === " ") {
                                            prev.remove();
                                        }
                                    }

                                    node.remove();
                                }
                            };

                            removeWords();

                            // Split paragraph after the anchor word (if any), so the next remaining word starts a new paragraph.
                            if (shouldInsertParagraphBreak && prevWordForBreakKey) {
                                const anchor = $getNodeByKey(prevWordForBreakKey);
                                if ($isWordNode(anchor)) {
                                    let p: any = anchor;
                                    while (p && typeof p.getType === "function" && p.getType() !== "paragraph") {
                                        p = p.getParent?.();
                                    }
                                    if (p && typeof p.getType === "function" && p.getType() === "paragraph") {
                                        const newP = $createParagraphNode();
                                        let move: any = anchor.getNextSibling();
                                        while (move) {
                                            const nextMove = move.getNextSibling?.();
                                            newP.append(move);
                                            move = nextMove;
                                        }
                                        p.insertAfter(newP);
                                    }
                                }
                            }
                        },
                        { tag: "historic" },
                    );

                    if (minStart < maxEnd) {
                        CompositionService.cutScriptLayerRange(minStart, maxEnd, { transcriptMode: "delete" });
                    }
                    if (prevWordForBreak && nextWordAfterSelection) {
                        CompositionService.addTranscriptParagraphBreak(prevWordForBreak.__payload);
                    }

                    return true;
                }

                // Collapsed caret: just insert a paragraph break (transcript-only).
                if (!prevWord || !nextWord) return true;

                // Optimistic split in Lexical.
                editor.update(
                    () => {
                        const anchor = $getNodeByKey(prevWord.getKey());
                        if (!$isWordNode(anchor)) return;
                        let p: any = anchor;
                        while (p && typeof p.getType === "function" && p.getType() !== "paragraph") {
                            p = p.getParent?.();
                        }
                        if (!p || typeof p.getType !== "function" || p.getType() !== "paragraph") return;

                        const newP = $createParagraphNode();
                        let move: any = anchor.getNextSibling();
                        while (move) {
                            const nextMove = move.getNextSibling?.();
                            newP.append(move);
                            move = nextMove;
                        }
                        p.insertAfter(newP);
                    },
                    { tag: "historic" },
                );

                CompositionService.addTranscriptParagraphBreak(prevWord.__payload);
                return true;
            },
            COMMAND_PRIORITY_HIGH,
        );
    }, [editor]);

    return null;
};

/**
 * BackspaceParagraphMergePlugin: Handles Backspace keypress to remove manual transcript paragraph breaks.
 *
 * Rules:
 * - Backspace with a collapsed caret at the START of a paragraph merges with the previous paragraph
 *   ONLY if that boundary is a manual transcript paragraph break.
 * - Otherwise, Backspace remains disabled (transcript is read-only).
 */
const BackspaceParagraphMergePlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        return editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                if (event.key !== "Backspace") return false;

                const target = event.target as HTMLElement | null;
                if (!target || !target.closest(".v2editor-scroll")) return false;

                // Only plain Backspace should affect paragraph breaks.
                if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return false;

                const selection = $getSelection();
                if (!selection || !$isRangeSelection(selection) || !selection.isCollapsed()) return false;

                const point = selection.anchor;
                const node = point.getNode();

                // Find containing paragraph.
                let paragraphNode: any = node;
                while (paragraphNode && typeof paragraphNode.getType === "function" && paragraphNode.getType() !== "paragraph") {
                    paragraphNode = paragraphNode.getParent?.();
                }
                if (!paragraphNode || typeof paragraphNode.getType !== "function" || paragraphNode.getType() !== "paragraph") return false;

                const findFirstWord = (n: any): WordNode | null => {
                    if (!n) return null;
                    if ($isWordNode(n)) return n as WordNode;
                    const first = n.getFirstChild?.();
                    if (!first) return null;
                    let child: any = first;
                    while (child) {
                        const found = findFirstWord(child);
                        if (found) return found;
                        child = child.getNextSibling?.();
                    }
                    return null;
                };

                const findLastWord = (n: any): WordNode | null => {
                    if (!n) return null;
                    if ($isWordNode(n)) return n as WordNode;
                    const last = n.getLastChild?.();
                    if (!last) return null;
                    let child: any = last;
                    while (child) {
                        const found = findLastWord(child);
                        if (found) return found;
                        child = child.getPreviousSibling?.();
                    }
                    return null;
                };

                const findPrevWordInDoc = (n: any): WordNode | null => {
                    let sibling: any = n?.getPreviousSibling?.();
                    while (sibling) {
                        const found = findLastWord(sibling);
                        if (found) return found;
                        sibling = sibling.getPreviousSibling?.();
                    }
                    let p: any = paragraphNode?.getPreviousSibling?.();
                    while (p) {
                        const found = findLastWord(p);
                        if (found) return found;
                        p = p.getPreviousSibling?.();
                    }
                    return null;
                };

                const firstWord = findFirstWord(paragraphNode);
                if (!firstWord) return false;

                // Only merge when caret is at the start of this paragraph.
                const caretAtStart =
                    point.type === "text"
                        ? point.key === firstWord.getKey() && point.offset === 0
                        : point.type === "element"
                          ? point.key === paragraphNode.getKey() && point.offset === 0
                          : false;
                if (!caretAtStart) return false;

                const prevWord = findPrevWordInDoc(firstWord);
                if (!prevWord) return false;

                const removed = CompositionService.removeTranscriptParagraphBreak(prevWord.__payload);
                if (!removed) return false;

                event.preventDefault();

                const prevWordKey = prevWord.getKey();
                const currentParaKey = paragraphNode.getKey();

                // Optimistically merge in Lexical immediately.
                editor.update(
                    () => {
                        const currentP: any = $getNodeByKey(currentParaKey);
                        if (!currentP || typeof currentP.getType !== "function" || currentP.getType() !== "paragraph") return;

                        const prevP: any = currentP.getPreviousSibling?.();
                        if (!prevP || typeof prevP.getType !== "function" || prevP.getType() !== "paragraph") return;

                        // Re-introduce the missing space between words if needed (break rendering omits it).
                        const prevNode = $getNodeByKey(prevWordKey);
                        const prevText = $isWordNode(prevNode) ? prevNode.getTextContent() : "";
                        const lastChild = prevP.getLastChild?.();
                        const lastIsSpace = lastChild instanceof TextNode && lastChild.getTextContent?.() === " ";
                        const needsSpace = !!prevText && !/^[.,!?;:)\]]$/.test(prevText);
                        if (needsSpace && !lastIsSpace) {
                            prevP.append($createTextNode(" "));
                        }

                        const children = currentP.getChildren?.() ?? [];
                        for (const child of children) {
                            prevP.append(child);
                        }
                        currentP.remove();
                    },
                    { tag: "historic" },
                );

                return true;
            },
            COMMAND_PRIORITY_HIGH,
        );
    }, [editor]);

    return null;
};

/**
 * RestrictedTranscriptInputPlugin:
 * Keep the transcript editor effectively read-only (no free typing / backspace edits),
 * while preserving a caret for controlled actions like "/" (scene split) and selection-based commands.
 */
const RestrictedTranscriptInputPlugin: React.FC = () => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        const unregisterKey = editor.registerCommand(
            KEY_DOWN_COMMAND,
            (event: KeyboardEvent) => {
                const target = event.target as HTMLElement | null;
                if (!target || !target.closest('.v2editor-scroll')) return false;

                // Let "/" be handled by SceneSplitPlugin.
                if (event.key === '/' || event.key === '#') return false;

                // Prevent free typing (printable chars) unless a modifier is held.
                const hasModifier = event.metaKey || event.ctrlKey || event.altKey;
                if (!hasModifier && event.key.length === 1) {
                    event.preventDefault();
                    return true;
                }

                // Prevent structural edits.
                if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    return true;
                }

                // Prevent default delete/backspace behavior (handled via selection-based shortcuts).
                if (event.key === 'Backspace' || event.key === 'Delete') {
                    event.preventDefault();
                    return true;
                }

                return false;
            },
            COMMAND_PRIORITY_LOW
        );

        const unregisterPaste = editor.registerCommand(
            PASTE_COMMAND,
            (event: any) => {
                try { event?.preventDefault?.(); } catch {}
                return true;
            },
            COMMAND_PRIORITY_HIGH
        );

        return () => {
            unregisterKey();
            unregisterPaste();
        };
    }, [editor]);

    return null;
};

// Track whether selection changes are internal (from transcript) or external (from waveform)
// This is a module-level ref to share between components
let isInternalSelectionUpdate = false;

/**
 * Helper to check if a transcription state indicates "in progress"
 */
function isTranscriptionInProgress(state: string | undefined): boolean {
    if (!state) return false;
    const stateLower = state.toLowerCase();
    return stateLower === 'queued' || 
           stateLower === 'processing' || 
           stateLower === 'client_extracting' || 
           stateLower === 'pending';
}

/**
 * TranscribingPlaceholder: Shows while script clip sources are being transcribed.
 * Displays a small "Transcribing..." indicator near the top of the editor, with % when available.
 */
const TranscribingPlaceholder: React.FC = observer(() => {
		    // Access stores for MobX reactivity.
		    const projectTracks = ProjectAssetsStore.projectTracks;
		    const _itemsLen = ScriptLayerStore.items.length;
		    const _updateTs = CompositionStateStore.updateTimestamp;
		    const _cutsVersion = ScriptLayerStore.cutsVersion;

	    const isTranscribing = useMemo(() => {
	        // Only show when one of the ScriptClip sources is transcribing.
	        // Avoid project-wide fallbacks because they cause false positives (unrelated uploads).
	        if (!CompositionService.isInitialized()) return false;

	        const comp: any = CompositionService.composition;
	        const scriptClipId = comp?.scriptLayer?.clipId || null;
	        if (!scriptClipId) return false;

	        const clip = CompositionService.getClipById(scriptClipId);
	        if (!clip || !isScriptClip(clip)) return false;

	        const scriptClip = clip as ScriptLayerClip;

	        // Prefer audioEntries (post-init) but fall back to inputConfigs (available pre-init)
	        // so we can still match track transcription state reliably.
	        const assetIds = new Set<string>();
		        try {
		            const audioEntries = Array.isArray((scriptClip as any).getAudioEntries?.())
		                ? (scriptClip as any).getAudioEntries()
		                : [];
		            for (const entry of audioEntries) {
		                const entryGain = Number(entry?.gain ?? 1);
		                if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;
		                const assetId = entry?.assetId ? String(entry.assetId) : "";
		                if (assetId) assetIds.add(assetId);
		            }
		        } catch {}

	        if (assetIds.size === 0) {
	            try {
		                const inputConfigs = Array.isArray((scriptClip as any).getInputConfigs?.())
		                    ? (scriptClip as any).getInputConfigs()
		                    : [];
		                for (const cfg of inputConfigs) {
		                    const cfgGain = Number(cfg?.gain ?? 1);
		                    if (Number.isFinite(cfgGain) && cfgGain <= 0.001) continue;
		                    const assetId = cfg?.assetId ? String(cfg.assetId) : "";
		                    if (assetId) assetIds.add(assetId);
		                }
		            } catch {}
		        }

	        if (assetIds.size === 0) return false;

	        for (const assetId of assetIds) {
	            const trackRecord = projectTracks.find((t: any) => t.id === assetId || t.trackId === assetId);
	            if (trackRecord && isTranscriptionInProgress(trackRecord.transcriptionState)) {
	                return true;
	            }
	        }

	        return false;
		    }, [projectTracks, _itemsLen, _updateTs, _cutsVersion]);

    if (!isTranscribing) return null;

    return (
        <Box
            sx={{
                position: "absolute",
                top: 8,
                left: 0,
                right: 0,
                zIndex: 2000,
                pointerEvents: "none",
                paddingLeft: "clamp(32px, calc((100% - 720px) / 2), 1000px)",
                paddingRight: "clamp(32px, calc((100% - 720px) / 2), 1000px)",
            }}
        >
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1.25,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "10px",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-subtle)",
                }}
            >
                <CircularProgress size={14} thickness={5} sx={{ color: "var(--app-text-muted)" }} />
                <Typography
                    sx={{
                        fontFamily: "Inter, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-primary)",
                    }}
                >
                    Transcribing...
                </Typography>
            </Box>
        </Box>
    );
});

/**
 * RangeSyncPlugin: Syncs external range selections (from waveform) to the Lexical editor.
 * When ApplicationStore.selectedScriptLayerRange changes from an external source,
 * this plugin finds the corresponding WordNodes and creates a selection spanning them.
 */
const RangeSyncPlugin: React.FC = observer(() => {
    const [editor] = useLexicalComposerContext();
    const selectedRange = ApplicationStore.selectedScriptLayerRange;
    const lastSyncedRangeRef = useRef<{ start: number; end: number } | null>(null);
    
    useEffect(() => {
        // Skip if this is an internal update (from transcript selection)
        if (isInternalSelectionUpdate) {
            return;
        }
        
        // Skip if range hasn't actually changed
        if (
            selectedRange?.start === lastSyncedRangeRef.current?.start &&
            selectedRange?.end === lastSyncedRangeRef.current?.end
        ) {
            return;
        }
        
        lastSyncedRangeRef.current = selectedRange;
        
        if (!selectedRange) {
            // Clear selection when range is null
            editor.update(() => {
                $setSelection(null);
            });
            return;
        }
        
        const { start, end } = selectedRange;

        // Convert timeline range -> script clip-local range for comparison with WordNode times.
        // This accounts for:
        // - ScriptClip trim-left/trim-right (range/offset)
        // - Cuts (segments collapse time)
        // - PlaybackRate (timelineDuration scaling)
        const EPS = 1e-3;
        const rate = ScriptLayerStore.playbackRate || 1;
        const timelineStart = Math.min(start, end);
        const timelineEnd = Math.max(start, end);

        const mappedStart = ScriptLayerStore.mapTimelineTimeToSourceTime(timelineStart);
        const mappedEnd = ScriptLayerStore.mapTimelineTimeToSourceTime(Math.max(timelineStart, timelineEnd - EPS));

        // If the selected range falls entirely inside an inserted gap (no source time),
        // clear the transcript selection instead of falling back to a linear mapping.
        if (mappedStart == null && mappedEnd == null) {
            editor.update(() => {
                $setSelection(null);
            });
            return;
        }

        const clipLocalStart = mappedStart ?? timelineStart * rate;
        const clipLocalEnd = mappedEnd ?? timelineEnd * rate;
        const srcStart = Math.min(clipLocalStart, clipLocalEnd);
        const srcEnd = Math.max(clipLocalStart, clipLocalEnd);

        editor.update(() => {
            const root = $getRoot();
            const allNodes = root.getAllTextNodes();
            const wordNodes = allNodes.filter(n => n instanceof WordNode) as WordNode[];

            // Find words that fall within the selected time range (converted to source time)
            const wordsInRange = wordNodes.filter(w => {
                const wStart = w.__payload.startSec ?? 0;
                const wEnd = w.__payload.endSec ?? wStart;
                // Word overlaps with range if word starts before range ends AND word ends after range starts
                return wStart < srcEnd && wEnd > srcStart;
            });
            
            if (wordsInRange.length === 0) {
                return;
            }
            
            // Sort by start time to get first and last
            wordsInRange.sort((a, b) => (a.__payload.startSec ?? 0) - (b.__payload.startSec ?? 0));
            
            const firstWord = wordsInRange[0];
            const lastWord = wordsInRange[wordsInRange.length - 1];
            
            // Create a selection spanning from first to last word
            const selection = $createRangeSelection();
            selection.anchor.set(firstWord.getKey(), 0, 'text');
            selection.focus.set(lastWord.getKey(), lastWord.getTextContentSize(), 'text');
            $setSelection(selection);
        });
    }, [editor, selectedRange]);

    return null;
});

/**
 * PlaybackHighlightPlugin: Highlights the current word during playback.
 * Uses debounced updates (50ms) and binary search for performance.
 */
const PlaybackHighlightPlugin: React.FC = observer(() => {
    const [editor] = useLexicalComposerContext();
    const highlightedKeyRef = useRef<string | null>(null);
    const isPlaying = CompositionStateStore.isPlaying;

    // Build a sorted index of words for binary search
    const wordIndex = useRef<Array<{ key: string; startSec: number; endSec: number }>>([]);

    // Rebuild word index when editor content changes
    useEffect(() => {
        const updateWordIndex = () => {
            editor.getEditorState().read(() => {
                const root = $getRoot();
                const allNodes = root.getAllTextNodes();
                const words = allNodes.filter(n => n instanceof WordNode) as WordNode[];

                const index = words
                    .map(w => {
                        const startSec = Number(w.__payload.startSec ?? 0);
                        const endSec = Number(w.__payload.endSec ?? startSec);
                        return { key: w.getKey(), startSec, endSec };
                    })
                    .filter(w => Number.isFinite(w.startSec) && Number.isFinite(w.endSec))
                    .sort((a, b) => a.startSec - b.startSec);

                // Ensure every entry has a positive duration so binary search can succeed.
                const EPS = 1e-3;
                for (let i = 0; i < index.length; i++) {
                    const start = index[i].startSec;
                    const nextStart = index[i + 1]?.startSec;
                    let end = index[i].endSec;
                    if (!Number.isFinite(end) || end <= start) {
                        end = Number.isFinite(nextStart) && nextStart > start ? nextStart : start + EPS;
                    }
                    index[i].endSec = end;
                }

                wordIndex.current = index;
            });
        };

        updateWordIndex();
        return editor.registerUpdateListener(updateWordIndex);
    }, [editor]);

    // Binary search to find word at given source time
    const findWordAtTime = (srcTime: number): string | null => {
        const index = wordIndex.current;
        if (index.length === 0) return null;

        let left = 0;
        let right = index.length - 1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const word = index[mid];

            if (srcTime >= word.startSec && srcTime < word.endSec) {
                return word.key;
            } else if (srcTime < word.startSec) {
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        return null;
    };

    // Update highlight based on playback position
    useEffect(() => {
        if (!isPlaying) {
            // Clear highlight when not playing
            if (highlightedKeyRef.current) {
                const el = editor.getElementByKey(highlightedKeyRef.current);
                if (el) el.classList.remove('word--highlighted');
                highlightedKeyRef.current = null;
            }
            return;
        }

        // Debounced update during playback
        const intervalId = setInterval(() => {
            // Convert timeline time to source time
            const srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(CompositionStateStore.currentTime);
            if (srcTime === null) {
                // Clear highlight when in an inserted gap/empty scene.
                if (highlightedKeyRef.current) {
                    const el = editor.getElementByKey(highlightedKeyRef.current);
                    if (el) el.classList.remove('word--highlighted');
                    highlightedKeyRef.current = null;
                }
                return;
            }

            const wordKey = findWordAtTime(srcTime);

            // Update highlight if word changed
            if (wordKey !== highlightedKeyRef.current) {
                // Remove old highlight
                if (highlightedKeyRef.current) {
                    const oldEl = editor.getElementByKey(highlightedKeyRef.current);
                    if (oldEl) oldEl.classList.remove('word--highlighted');
                }

                // Add new highlight and scroll into view
                if (wordKey) {
                    const newEl = editor.getElementByKey(wordKey);
                    if (newEl) {
                        newEl.classList.add('word--highlighted');
                        // Scroll into view with some margin, only if needed
                        const scrollContainer = document.querySelector('.v2editor-scroll') as HTMLElement;
                        if (scrollContainer) {
                            const elRect = newEl.getBoundingClientRect();
                            const containerRect = scrollContainer.getBoundingClientRect();
                            const margin = 60; // pixels from top/bottom edge before scrolling

                            if (elRect.top < containerRect.top + margin) {
                                // Element is above visible area
                                newEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            } else if (elRect.bottom > containerRect.bottom - margin) {
                                // Element is below visible area
                                newEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }
                        }
                    }
                }

                highlightedKeyRef.current = wordKey;
            }
        }, 50); // Update every 50ms

        return () => clearInterval(intervalId);
    }, [editor, isPlaying]);

    return null;
});

type TranscriptExportEntry = {
    type: "speaker" | "marker" | "text";
    text: string;
};

const NO_LEADING_SPACE_PUNCTUATION_RE = /^[.,!?;:)\]%]/;

function appendTranscriptToken(line: string, token: string): string {
    const trimmedToken = String(token ?? "").trim();
    if (!trimmedToken) return line;
    if (!line) return trimmedToken;
    if (NO_LEADING_SPACE_PUNCTUATION_RE.test(trimmedToken)) return `${line}${trimmedToken}`;
    return `${line} ${trimmedToken}`;
}

function hasBackgroundHighlight(style: string): boolean {
    const match = String(style ?? "").match(/background-color\s*:\s*([^;]+)/i);
    if (!match) return false;
    const color = String(match[1] ?? "").trim().toLowerCase();
    return Boolean(
        color &&
        color !== "transparent" &&
        color !== "none" &&
        color !== "initial" &&
        color !== "inherit" &&
        color !== "unset" &&
        color !== "rgba(0, 0, 0, 0)"
    );
}

function formatTranscriptEntries(entries: TranscriptExportEntry[]): string {
    if (!entries.length) return "";
    const lines: string[] = [];
    for (let idx = 0; idx < entries.length; idx++) {
        const current = entries[idx];
        lines.push(current.text);
        const next = entries[idx + 1];
        if (!next) continue;
        if (!(current.type === "speaker" && next.type === "text")) {
            lines.push("");
        }
    }
    return lines.join("\n").trim();
}

const TranscriptExportRegistrationPlugin: React.FC<{
    onReady?: (builder: TranscriptExporter | null) => void;
}> = ({ onReady }) => {
    const [editor] = useLexicalComposerContext();

    const buildTranscript = useMemo<TranscriptExporter>(() => {
        return (options: TranscriptExportOptions) => {
            const safeOptions: TranscriptExportOptions = {
                includeMarkers: Boolean(options?.includeMarkers),
                includeIgnoredText: Boolean(options?.includeIgnoredText),
                includeHighlights: Boolean(options?.includeHighlights),
                includeSpeakerLabels: Boolean(options?.includeSpeakerLabels),
            };

            const entries: TranscriptExportEntry[] = [];
            editor.getEditorState().read(() => {
                const root = $getRoot();
                for (const block of root.getChildren()) {
                    const children = (block as any)?.getChildren?.();
                    if (!Array.isArray(children) || children.length === 0) continue;

                    let line = "";

                    for (const child of children) {
                        if (child instanceof SceneBreakNode) continue;

                        if ($isMarkerNode(child)) {
                            if (!safeOptions.includeMarkers) continue;
                            const markerTitle = String((child as any).getTitle?.() ?? child.getTextContent() ?? "").trim();
                            if (!markerTitle) continue;
                            if (line.trim()) {
                                entries.push({ type: "text", text: line.trim() });
                                line = "";
                            }
                            entries.push({ type: "marker", text: `# ${markerTitle}` });
                            continue;
                        }

                        if ($isSpeakerNode(child)) {
                            if (!safeOptions.includeSpeakerLabels) continue;
                            const speakerName = String((child as any).getDisplayName?.() ?? child.getTextContent() ?? "").trim();
                            if (!speakerName) continue;
                            if (line.trim()) {
                                entries.push({ type: "text", text: line.trim() });
                                line = "";
                            }
                            entries.push({ type: "speaker", text: speakerName });
                            continue;
                        }

                        if ($isWordNode(child)) {
                            const tokenText = String(child.getTextContent() ?? "").trim();
                            if (!tokenText) continue;

                            const style = String((child as any).getStyle?.() ?? "");
                            const isIgnored = /line-through/i.test(style);
                            if (isIgnored && !safeOptions.includeIgnoredText) continue;

                            const isHighlighted = hasBackgroundHighlight(style);

                            let token = tokenText;
                            if (isIgnored && safeOptions.includeIgnoredText) token = `~~${token}~~`;
                            if (isHighlighted && safeOptions.includeHighlights) token = `[[${token}]]`;

                            line = appendTranscriptToken(line, token);
                        }
                    }

                    if (line.trim()) {
                        entries.push({ type: "text", text: line.trim() });
                    }
                }
            });

            return formatTranscriptEntries(entries);
        };
    }, [editor]);

    useEffect(() => {
        onReady?.(buildTranscript);
        return () => onReady?.(null);
    }, [onReady, buildTranscript]);

    return null;
};

const V2LexicalTextEditor: React.FC<V2LexicalTextEditorProps> = observer(({
                                                                     words,
    readOnly = true,  // Default to read-only
    onDuplicateToNewComposition,
    onTranscriptExporterReady,
                                                                 }) => {
    const editorOverlayRef = useRef<HTMLDivElement | null>(null);
    const initialConfig: InitialConfigType = {
        namespace: "v2-text-editor",
        // Keep Lexical editable so we can show a caret; we still block free typing via RestrictedTranscriptInputPlugin.
        editable: true,
	        onError: (e) => console.error(e),
	        theme,
	        nodes: [WordNode, SpeakerNode, MarkerNode, SceneBreakNode],
	    };

    const onChange = (_editorState: EditorState) => {
        _editorState.read(() => {
            const selection = $getSelection();
            if (!selection || !$isRangeSelection(selection) || selection.isCollapsed()) {
                // Mark as internal update to prevent RangeSyncPlugin loop
                isInternalSelectionUpdate = true;
                ApplicationStore.setSelectedScriptLayerRange(null);
                setTimeout(() => { isInternalSelectionUpdate = false; }, 0);
                return;
            }

            const nodes = selection.getNodes();
            const wordNodes = nodes.filter(n => $isWordNode(n)) as WordNode[];
            if (wordNodes.length === 0) {
                // hide UI
                return;
            }

            const words: V2TranscriptWord[] = wordNodes.map((wn) => {
                // payload on node
                const p: any = (wn as any).__payload ?? {};
                const s = Number(p.startSec ?? p.baseStartSec ?? Number.NaN);
                const e = Number.isFinite(Number(p.endSec)) ? Number(p.endSec) : s;
                return { text: wn.getTextContent(), startSec: s, endSec: e, speaker: p.speaker };
            });

            // time span (in source time)
            const valid = words.filter(w => Number.isFinite(w.startSec));
            const startSec = Math.min(...valid.map(w => w.startSec));
            const endSec = Math.max(...valid.map(w => w.endSec ?? w.startSec));

            // Convert script clip-local range -> timeline range for waveform display.
            // This accounts for trim-left/trim-right and cuts via segments.
            const EPS = 1e-3;
            const timelineStart =
                ScriptLayerStore.mapSourceTimeToCompositionTime(startSec) ??
                (startSec / (ScriptLayerStore.playbackRate || 1));
            const timelineEnd =
                ScriptLayerStore.mapSourceTimeToCompositionTime(Math.max(startSec, endSec - EPS)) ??
                (endSec / (ScriptLayerStore.playbackRate || 1));

            // Mark as internal update to prevent RangeSyncPlugin loop
            isInternalSelectionUpdate = true;
            ApplicationStore.setSelectedScriptLayerRange({start: timelineStart, end: timelineEnd});
            setTimeout(() => { isInternalSelectionUpdate = false; }, 0);
        });
    };

    const editorInputStyle: React.CSSProperties = {
        textAlign: 'left',
        paddingTop: '40px',
        paddingBottom: '100px',
        alignItems: 'center',
        overflowY: 'auto',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        paddingLeft: 'clamp(28px, calc((100% - 680px) / 2), 1000px)',
        paddingRight: 'clamp(28px, calc((100% - 680px) / 2), 1000px)',
        scrollbarGutter: 'stable',
        outline: 'none',
        // Typography
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 'clamp(15px, 0.5vw + 12px, 16px)',
        lineHeight: '1.7',
        color: 'var(--app-text-primary)',
        letterSpacing: '-0.005em',
    };

	    const handleWordNodeClick = async (_event: MouseEvent, editor: LexicalEditor, nodeKey: string) => {
	        const node = $getNodeByKey(nodeKey) as WordNode;
	        const payload = node.__payload;
	        const wordStartSec = payload.startSec ?? payload.srcStartSec ?? null;
	        const wordEndSec = payload.endSec ?? payload.srcEndSec ?? null;
	        console.log('[TextEditor] Word click timestamps:', {
	            startSec: wordStartSec,
	            endSec: wordEndSec,
	        });
	        
	        // Use clip-local times for cut detection and seeking.
	        // (srcStartSec/srcEndSec are source-track coordinates and do NOT include empty-scene offsets.)
		        const clipTime = getWordStartClipTimeSec(payload) ?? 0;
        
        // If word is in a strikethrough cut, select the cut for restore
        if (ScriptLayerStore.isTimeInStrikethroughCut(clipTime)) {
            const cut = ScriptLayerStore.getCutAtTime(clipTime);
            if (cut) {
                console.log('[TextEditor] Clicked strikethrough word, selecting cut for restore:', cut.id);
                ScriptLayerStore.setSelectedCut(cut.id);
                
                // Select all words in this cut's range in the editor
                editor.update(() => {
                    const root = $getRoot();
                    const allNodes = root.getAllTextNodes();
                    const wordNodes = allNodes.filter(n => n instanceof WordNode) as WordNode[];
                    
                    // Find words that fall within the cut's time range (clip-local time)
                    const cutWords = wordNodes.filter(w => {
                        const wTime = w.__payload.startSec ?? w.__payload.srcStartSec ?? 0;
                        return wTime >= cut.startSec && wTime < cut.endSec;
                    });
                    
                    if (cutWords.length > 0) {
                        // Create a selection spanning all words in the cut
                        const firstWord = cutWords[0];
                        const lastWord = cutWords[cutWords.length - 1];
                        const selection = $createRangeSelection();
                        selection.anchor.set(firstWord.getKey(), 0, 'text');
                        selection.focus.set(lastWord.getKey(), lastWord.getTextContentSize(), 'text');
                        $setSelection(selection);
                    }
                });
            }
            return;
        }
        
        // Clear any selected cut when clicking a normal word
        if (ScriptLayerStore.selectedCutId) {
            ScriptLayerStore.setSelectedCut(null);
        }
        
        // Map clip-local time to timeline time through segments and playbackRate.
	        const midTime = getWordMidClipTimeSec(payload) ?? 0;

        // Use ScriptLayerStore helper to map through segments and apply playbackRate
        const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(midTime);
        
        const clickTime = compTime !== null
            ? compTime
            : midTime / ScriptLayerStore.playbackRate;  // Fallback with playbackRate
        
	        await CompositionService.seek(clickTime);
	    }

	    const handleSceneBreakNodeClick = async (_event: MouseEvent, _editor: LexicalEditor, nodeKey: string) => {
	        let payload: any = null;
	        _editor.getEditorState().read(() => {
	            const node = $getNodeByKey(nodeKey) as any;
	            payload = (node as any)?.__payload ?? null;
	        });

	        const sceneIndex = Number(payload?.sceneIndex ?? NaN);
	        const startSrc = Number(payload?.startSec ?? NaN);
	        const endSrc = Number(payload?.endSec ?? startSrc);
	        if (!Number.isFinite(sceneIndex) || !Number.isFinite(startSrc)) return;

	        const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
	        const toSeekTimeSec = (srcTimeSec: number): number => {
	            const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(srcTimeSec);
	            return mapped != null ? mapped : srcTimeSec / rate;
	        };

	        const startTimeline = toSeekTimeSec(startSrc);
	        ApplicationStore.setSelectedSceneIndex(sceneIndex);

	        await CompositionService.seek((Number.isFinite(startTimeline) ? startTimeline : 0) + 0.001);
	    };

	    const handleSceneBreakNodeContextMenu = (_event: MouseEvent, _editor: LexicalEditor, nodeKey: string) => {
	        _event.preventDefault();
	        _event.stopPropagation();

	        let payload: any = null;
	        _editor.getEditorState().read(() => {
	            const node = $getNodeByKey(nodeKey) as any;
	            payload = (node as any)?.__payload ?? null;
	        });

	        const sceneIndex = Number(payload?.sceneIndex ?? NaN);
	        const startSrc = Number(payload?.startSec ?? NaN);
	        const endSrc = Number(payload?.endSec ?? startSrc);
	        if (!Number.isFinite(sceneIndex) || !Number.isFinite(startSrc)) return;

	        const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
	        const toSeekTimeSec = (srcTimeSec: number): number => {
	            const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(srcTimeSec);
	            return mapped != null ? mapped : srcTimeSec / rate;
	        };

	        const options: any[] = [];
	        options.push({
	            title: "Change Layout",
	            onClick: () => {
	                try {
	                    CompositionService.seek(toSeekTimeSec(startSrc) + 0.001);
	                } catch {}
	                try {
	                    window.dispatchEvent(
	                        new CustomEvent("openPropertiesSidebarLayout", { detail: { sceneIndex } }),
	                    );
	                } catch {}
	                ContextMenuStore.hide();
	            },
	        });
	        options.push({
	            title: "Add scene above",
	            onClick: () => {
	                CompositionService.insertEmptyScriptScene(startSrc, 5);
	                ContextMenuStore.hide();
	            },
	        });
	        options.push({
	            title: "Add scene below",
	            onClick: () => {
	                CompositionService.insertEmptyScriptScene(endSrc, 5);
	                ContextMenuStore.hide();
	            },
	        });
	        if (sceneIndex > 0) {
	            options.push({
	                title: "Merge with scene above",
	                onClick: () => {
	                    const ok = CompositionService.mergeScriptLayerSceneWithPrevious(sceneIndex);
	                    void ok;
	                    ContextMenuStore.hide();
	                },
	            });
	        }
	        if (sceneIndex < ScriptLayerStore.scenes.length - 1) {
	            options.push({
	                title: "Merge with scene below",
	                onClick: () => {
	                    const ok = CompositionService.mergeScriptLayerSceneWithNext(sceneIndex);
	                    void ok;
	                    ContextMenuStore.hide();
	                },
	            });
	        }
	        options.push({
	            title: "Delete Scene",
	            onClick: () => {
	                try {
	                    const clip: any = CompositionService.getScriptLayerClip?.();
	                    const clipInSec = Number(clip?.range?.[0]?.seconds ?? 0);
	                    const clipOutSec = Number(clip?.range?.[1]?.seconds ?? clip?.duration?.seconds ?? clipInSec);
	                    const EPS = 1e-3;
	                    const engineScenes = typeof clip?.getScenes === "function" ? clip.getScenes() : [];
	                    const engineScene = engineScenes.find((s: any) => s?.index === sceneIndex);
	                    if (engineScene) {
	                        const start = Number(engineScene.startSec ?? 0);
	                        const end = Number(engineScene.endSec ?? start);
	                        const coversWholeClip =
	                            Number.isFinite(start) &&
	                            Number.isFinite(end) &&
	                            Math.abs(start - clipInSec) <= EPS &&
	                            Math.abs(end - clipOutSec) <= EPS;

	                        if (coversWholeClip) {
	                            CompositionService.deleteClipById(String(clip?.id));
	                        } else {
	                            CompositionService.deleteScriptLayerScene(sceneIndex, { transcriptMode: "delete" });
	                        }
	                    }
	                } catch {}
	                ContextMenuStore.hide();
	            },
	        });

	        ContextMenuStore.show(_event.clientX, _event.clientY, options);
	    };

		    const handleMarkerNodeClick = async (_event: MouseEvent, _editor: LexicalEditor, nodeKey: string) => {
		        _event.preventDefault();
		        _event.stopPropagation();

	        if (_event.detail >= 2) {
	            // Double-click should be handled by the dblclick handler (rename) without seeking.
	            if (markerClickTimeoutRef.current != null) {
	                window.clearTimeout(markerClickTimeoutRef.current);
	                markerClickTimeoutRef.current = null;
	            }
	            return;
	        }

	        const target = _event.target as HTMLElement;
	        if (target.classList.contains("marker-delete-btn")) {
	            const markerId = String(target.dataset.markerId ?? "");
	            if (markerId) {
	                CompositionService.removeTranscriptMarker(markerId);
            }
            return;
        }

        let timeSec = 0;
        _editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey) as any;
            const payload = (node as any)?.__payload;
            timeSec = Number(payload?.timeSec ?? 0);
	        });

	        if (markerClickTimeoutRef.current != null) {
	            window.clearTimeout(markerClickTimeoutRef.current);
	            markerClickTimeoutRef.current = null;
	        }
	        markerClickTimeoutRef.current = window.setTimeout(() => {
	            markerClickTimeoutRef.current = null;
	            const compTime = ScriptLayerStore.mapSourceTimeToCompositionTime(timeSec);
	            const clickTime = compTime !== null ? compTime : timeSec / ScriptLayerStore.playbackRate;
	            CompositionService.seek(clickTime);
	        }, 250);
	    };

	    // Marker renaming state
	    const [editingMarker, setEditingMarker] = useState<{
	        nodeKey: string;
	        anchorPos: { top: number; left: number };
	        markerId: string;
	        title: string;
	    } | null>(null);
	    const [markerEditValue, setMarkerEditValue] = useState("");
	    const markerEditInputRef = useRef<HTMLInputElement>(null);
	    const markerClickTimeoutRef = useRef<number | null>(null);
    const markerEditSavedScrollTopRef = useRef<number | null>(null);

	    const handleMarkerNodeDoubleClick = (_event: MouseEvent, editor: LexicalEditor, nodeKey: string) => {
	        _event.preventDefault();
	        _event.stopPropagation();

	        if (markerClickTimeoutRef.current != null) {
	            window.clearTimeout(markerClickTimeoutRef.current);
	            markerClickTimeoutRef.current = null;
	        }

	        const target = _event.target as HTMLElement;
	        if (target.classList.contains("marker-delete-btn")) return;

	        const scrollContainer = document.querySelector(".v2editor-scroll") as HTMLElement | null;
	        const savedScrollTop = scrollContainer?.scrollTop ?? 0;
	        markerEditSavedScrollTopRef.current = savedScrollTop;

	        let anchorEl = target;
	        if (target.classList.contains("marker-text")) {
	            anchorEl = target.parentElement as HTMLElement;
	        }
	        const containerRect = editorOverlayRef.current?.getBoundingClientRect?.() ?? { top: 0, left: 0 };
	        const anchorRect = anchorEl.getBoundingClientRect();
	        const anchorPos = {
	            top: anchorRect.top - containerRect.top,
	            left: anchorRect.left - containerRect.left,
	        };

	        editor.getEditorState().read(() => {
	            const node = $getNodeByKey(nodeKey) as any;
	            const payload = (node as any)?.__payload;
	            const markerId = String(payload?.markerId ?? "");
	            const title = String(payload?.title ?? "").trim() || "Marker";
	            if (!markerId) return;
	            setEditingMarker({ nodeKey, anchorPos, markerId, title });
	            setMarkerEditValue(title);
	        });

	        // Prevent UI state updates (Popover + focus) from snapping the transcript scroll position.
	        requestAnimationFrame(() => {
	            if (scrollContainer) {
	                scrollContainer.scrollTop = savedScrollTop;
	                requestAnimationFrame(() => {
	                    if (scrollContainer) {
	                        scrollContainer.scrollTop = savedScrollTop;
	                    }
	                });
	            }
	        });
	    };

    // Speaker label editing state
    const [editingSpeaker, setEditingSpeaker] = useState<{
        nodeKey: string;
        anchorEl: HTMLElement;
        displayName: string;
        sourceIndex: number;
        speakerId?: string | number;  // ElevenLabs returns strings like "speaker_0"
        clipId: string;
        color: string;
        overrideId?: string;
        isPending?: boolean; // True if this is a new speaker override being created
    } | null>(null);
    const [editValue, setEditValue] = useState('');
    const editInputRef = useRef<HTMLInputElement>(null);

    const speakerPayloadToMuteTarget = (payload: any): SpeakerMuteTarget => {
        const sourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
        const diarizationSpeakerId =
            payload?.speakerId != null && String(payload.speakerId).trim()
                ? String(payload.speakerId).trim()
                : undefined;
        const speakerId =
            typeof payload?.firestoreSpeakerId === "string" && String(payload.firestoreSpeakerId).trim()
                ? String(payload.firestoreSpeakerId).trim()
                : undefined;
        const speakerName =
            typeof payload?.displayName === "string" && String(payload.displayName).trim()
                ? String(payload.displayName).trim()
                : undefined;

        return {
            sourceIndex,
            diarizationSpeakerId,
            speakerId,
            speakerName,
        };
    };

    const speakerPayloadMatchesMuteTarget = (payload: any, target: SpeakerMuteTarget): boolean => {
        const targetSpeakerId = String(target?.speakerId ?? "").trim();
        if (targetSpeakerId) {
            const payloadSpeakerId = String(payload?.firestoreSpeakerId ?? "").trim();
            return !!payloadSpeakerId && payloadSpeakerId === targetSpeakerId;
        }

        const targetSourceIndex = Number.isFinite(target?.sourceIndex) ? Number(target?.sourceIndex) : undefined;
        const targetDiarizationSpeakerId = String(target?.diarizationSpeakerId ?? "").trim();
        if (targetSourceIndex != null && targetDiarizationSpeakerId) {
            const payloadSourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
            const payloadDiarizationSpeakerId =
                payload?.speakerId != null && String(payload.speakerId).trim()
                    ? String(payload.speakerId).trim()
                    : "";
            return payloadSourceIndex === targetSourceIndex && payloadDiarizationSpeakerId === targetDiarizationSpeakerId;
        }

        const targetName = String(target?.speakerName ?? "").trim().toLowerCase();
        if (targetSourceIndex != null && targetName) {
            const payloadSourceIndex = Number.isFinite(payload?.sourceIndex) ? Number(payload.sourceIndex) : undefined;
            const payloadName = String(payload?.displayName ?? "").trim().toLowerCase();
            return payloadSourceIndex === targetSourceIndex && !!payloadName && payloadName === targetName;
        }

        return false;
    };

    const collectSpeakerMuteRangesForTarget = (editor: LexicalEditor, target: SpeakerMuteTarget): Array<{ startSec: number; endSec: number }> => {
        const rawRanges: Array<{ startSec: number; endSec: number }> = [];
        editor.getEditorState().read(() => {
            const root = $getRoot();
            for (const block of root.getChildren()) {
                const children = (block as any)?.getChildren?.();
                if (!Array.isArray(children) || children.length === 0) continue;
                for (const child of children) {
                    if (!$isSpeakerNode(child)) continue;
                    const payload = (child as any)?.__payload;
                    if (!speakerPayloadMatchesMuteTarget(payload, target)) continue;

                    const startSec = Number(payload?.segmentStartSec ?? NaN);
                    const endSec = Number(payload?.segmentEndSec ?? NaN);
                    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) continue;
                    rawRanges.push({ startSec, endSec });
                }
            }
        });

        if (rawRanges.length === 0) return [];
        rawRanges.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
        const merged: Array<{ startSec: number; endSec: number }> = [];
        const EPS = 1e-3;
        for (const r of rawRanges) {
            if (!merged.length) {
                merged.push({ ...r });
                continue;
            }
            const last = merged[merged.length - 1];
            if (r.startSec <= last.endSec + EPS) {
                last.endSec = Math.max(last.endSec, r.endSec);
            } else {
                merged.push({ ...r });
            }
        }
        return merged;
    };

    const handleSpeakerNodeClick = (_event: MouseEvent, editor: LexicalEditor, nodeKey: string) => {
        // Always prevent default and stop propagation to avoid Lexical handling the click
        // which can cause scroll jumps and focus issues
        _event.preventDefault();
        _event.stopPropagation();
        
        // Save scroll position before any state changes
        const scrollContainer = document.querySelector('.v2editor-scroll') as HTMLElement;
        const savedScrollTop = scrollContainer?.scrollTop ?? 0;
        
        // Check if click was on the delete button
        const target = _event.target as HTMLElement;
        if (target.classList.contains('speaker-delete-btn')) {
            // Check if this is a pending speaker being cancelled
            if (target.dataset.isPending === 'true') {
                ScriptLayerStore.setPendingSpeakerOverride(null);
                return;
            }
            
            // Check if this is a speaker override being deleted
            const overrideId = target.dataset.overrideId;
            if (overrideId) {
                ScriptLayerStore.removeSpeakerOverride(overrideId);
                return;
            }
            
            // For original speakers (not overrides), hide the segment
            // by adding a "no speaker" override using the time range from data attributes
            const startSec = target.dataset.segmentStartSec;
            const endSec = target.dataset.segmentEndSec;
            const diarizationSpeakerId = target.dataset.diarizationSpeakerId;
            const sourceIndex = target.dataset.sourceIndex != null ? Number(target.dataset.sourceIndex) : undefined;

            if (startSec !== undefined && endSec !== undefined) {
                ScriptLayerStore.addSpeakerOverride(
                    parseFloat(startSec),
                    parseFloat(endSec),
                    '__hidden__',
                    undefined, // color
                    [], // usedSourceColors
                    undefined, // speakerId (Firestore)
                    diarizationSpeakerId,
                    sourceIndex
                );
            }
            return;
        }
        
        // Find the speaker label container (might have clicked on the text span inside)
        let anchorEl = target;
        if (target.classList.contains('speaker-text')) {
            anchorEl = target.parentElement as HTMLElement;
        }
        
        editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey) as SpeakerNode;
            if (!node) return;
            
            const payload = node.__payload;
            
            // Check if this is a pending speaker override (empty name means we need to edit)
            const isPending = ScriptLayerStore.pendingSpeakerOverride !== null && !payload.displayName;
            
            setEditingSpeaker({
                nodeKey,
                anchorEl: anchorEl,
                displayName: payload.displayName,
                sourceIndex: payload.sourceIndex,
                speakerId: payload.speakerId,
                clipId: payload.clipId,
                color: payload.color,
                overrideId: payload.overrideId,
                isPending,
            });
            setEditValue(payload.displayName || '');
        });
        
        // Restore scroll position after state update - use multiple frames to ensure it sticks
        requestAnimationFrame(() => {
            if (scrollContainer) {
                scrollContainer.scrollTop = savedScrollTop;
                // Double-check after another frame in case React re-render affected it
                requestAnimationFrame(() => {
                    if (scrollContainer) {
                        scrollContainer.scrollTop = savedScrollTop;
                    }
                });
            }
        });
    };

    const handleSpeakerNodeContextMenu = (_event: MouseEvent, editor: LexicalEditor, nodeKey: string) => {
        _event.preventDefault();
        _event.stopPropagation();

        let payload: any = null;
        editor.getEditorState().read(() => {
            const node = $getNodeByKey(nodeKey) as SpeakerNode;
            payload = node ? node.__payload : null;
        });
        if (!payload) return;

        const displayName = String(payload?.displayName ?? "").trim();
        if (!displayName || displayName === "__hidden__") return;

        const target = speakerPayloadToMuteTarget(payload);
        const isMuted = ScriptLayerStore.isSpeakerMuted(target);

        ContextMenuStore.show(_event.clientX, _event.clientY, [
            {
                title: isMuted ? "Unmute Speaker" : "Mute Speaker",
                onClick: () => {
                    if (isMuted) {
                        ScriptLayerStore.clearSpeakerMuted(target);
                        ContextMenuStore.hide();
                        return;
                    }

                    let ranges = collectSpeakerMuteRangesForTarget(editor, target);
                    if (ranges.length === 0) {
                        const startSec = Number(payload?.segmentStartSec ?? NaN);
                        const endSec = Number(payload?.segmentEndSec ?? NaN);
                        if (Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec) {
                            ranges = [{ startSec, endSec }];
                        }
                    }
                    if (ranges.length > 0) {
                        ScriptLayerStore.setSpeakerMuted(target, ranges);
                    }
                    ContextMenuStore.hide();
                },
            },
        ]);
    };
    
    /**
     * Handle deleting a speaker override (clicking the X button).
     */
    const handleDeleteSpeakerOverride = (overrideId: string) => {
        ScriptLayerStore.removeSpeakerOverride(overrideId);
        setEditingSpeaker(null);
    };

    useEffect(() => {
        if (editingSpeaker && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingSpeaker]);

	    useEffect(() => {
	        if (editingMarker && markerEditInputRef.current) {
	            markerEditInputRef.current.focus({ preventScroll: true });
	            markerEditInputRef.current.select();
	        }
	        const savedScrollTop = markerEditSavedScrollTopRef.current;
	        if (savedScrollTop != null) {
	            const scrollContainer = document.querySelector(".v2editor-scroll") as HTMLElement | null;
	            requestAnimationFrame(() => {
	                if (scrollContainer) {
	                    scrollContainer.scrollTop = savedScrollTop;
	                    requestAnimationFrame(() => {
	                        if (scrollContainer) {
	                            scrollContainer.scrollTop = savedScrollTop;
	                        }
	                    });
	                }
	            });
	        }
	        if (!editingMarker) {
	            markerEditSavedScrollTopRef.current = null;
	        }
	    }, [editingMarker]);

	    const applyMarkerRename = () => {
	        if (!editingMarker) return;
	        const scrollContainer = document.querySelector(".v2editor-scroll") as HTMLElement | null;
	        const savedScrollTop = scrollContainer?.scrollTop ?? markerEditSavedScrollTopRef.current ?? 0;
	        const next = String(markerEditValue ?? "").trim();
	        if (!next) {
	            setEditingMarker(null);
	            return;
	        }
	        CompositionService.renameTranscriptMarker(editingMarker.markerId, next);
	        setEditingMarker(null);
	        requestAnimationFrame(() => {
	            if (scrollContainer) {
	                scrollContainer.scrollTop = savedScrollTop;
	                requestAnimationFrame(() => {
	                    if (scrollContainer) {
	                        scrollContainer.scrollTop = savedScrollTop;
	                    }
	                });
	            }
	        });
	    };

    // Auto-open edit mode when a pending speaker override is created
    useEffect(() => {
        if (ScriptLayerStore.pendingSpeakerOverride && !editingSpeaker) {
            // Save scroll position before any DOM operations
            const scrollContainer = document.querySelector('.v2editor-scroll') as HTMLElement;
            const savedScrollTop = scrollContainer?.scrollTop ?? 0;
            
            // Wait a short moment for the DOM to update, then find and click the pending speaker label
            const timer = setTimeout(() => {
                const pendingLabel = document.querySelector('.speaker-label-pending');
                if (pendingLabel) {
                    // Get the color from the pending label's computed style (more reliable than inline style)
                    const computedStyle = window.getComputedStyle(pendingLabel);
                    const computedColor = computedStyle.color;
                    
                    // Convert rgb to hex if needed
                    let labelColor: string;
                    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                    if (rgbMatch) {
                        const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
                        const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
                        const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
                        labelColor = `#${r}${g}${b}`.toUpperCase();
                    } else {
                        // Fallback: try to extract from inline style or use next available color
                        const labelStyle = pendingLabel.getAttribute('style') || '';
                        const hexMatch = labelStyle.match(/color:\s*(#[0-9A-Fa-f]{6})/i);
                        labelColor = hexMatch ? hexMatch[1].toUpperCase() : ScriptLayerStore.getNextSpeakerColor();
                    }
                    
                    console.log('[V2LexicalTextEditor] Auto-open pending speaker - extracted color:', labelColor, 'from computed:', computedColor);
                    
                    setEditingSpeaker({
                        nodeKey: '',
                        anchorEl: pendingLabel as HTMLElement,
                        displayName: '',
                        sourceIndex: 0,
                        clipId: '',
                        color: labelColor,
                        isPending: true,
                    });
                    setEditValue('');
                    
                    // Restore scroll position after popover is set up
                    requestAnimationFrame(() => {
                        if (scrollContainer && savedScrollTop > 0) {
                            scrollContainer.scrollTop = savedScrollTop;
                        }
                    });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [ScriptLayerStore.pendingSpeakerOverride]);

    // Get CreateSpeaker from AssetStore
    const { CreateSpeaker } = useAssetStore();
    
    /**
     * Handle selecting an existing speaker from the project.
     */
    const handleSelectExistingSpeaker = async (speaker: any) => {
        // Save scroll position before any state changes
        const scrollContainer = document.querySelector('.v2editor-scroll') as HTMLElement;
        const savedScrollTop = scrollContainer?.scrollTop ?? 0;
        
        const restoreScroll = () => {
            requestAnimationFrame(() => {
                if (scrollContainer) {
                    scrollContainer.scrollTop = savedScrollTop;
                    requestAnimationFrame(() => {
                        if (scrollContainer) {
                            scrollContainer.scrollTop = savedScrollTop;
                        }
                    });
                }
            });
        };
        
        if (!editingSpeaker) {
            restoreScroll();
            return;
        }
        
        const speakerId = speaker.speakerId || speaker.id;
        const speakerName = speaker.name;
        
        // Handle pending speaker override - create new override with existing speaker
        if (editingSpeaker.isPending && ScriptLayerStore.pendingSpeakerOverride) {
            const { startSec, endSec } = ScriptLayerStore.pendingSpeakerOverride;
            
            // Use the same color as other overrides with this speaker (for consistency/merging)
            const existingOverrideWithSameSpeaker = ScriptLayerStore.speakerOverrides.find(
                o => o.speakerId === speakerId
            );
            const color = existingOverrideWithSameSpeaker?.color || editingSpeaker.color;
            
            // editingSpeaker.speakerId is the diarization speaker ID (e.g., "speaker_0")
            const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;
            console.log('[V2LexicalTextEditor] Selecting existing speaker for pending override:', {
                speakerId,
                diarizationSpeakerId,
                sourceIndex: editingSpeaker.sourceIndex,
                name: speakerName,
                color,
                startSec,
                endSec,
            });
            ScriptLayerStore.addSpeakerOverride(
                startSec,
                endSec,
                speakerName,
                color,
                [],
                speakerId,
                diarizationSpeakerId,
                editingSpeaker.sourceIndex
            );
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }
        
        // Handle existing override - check if it's a "global" override (covers all instances)
        if (editingSpeaker.overrideId) {
            const existingOverride = ScriptLayerStore.speakerOverrides.find(o => o.id === editingSpeaker.overrideId);
            const isGlobalOverride = existingOverride && existingOverride.endSec > 1000000000; // MAX_SAFE_INTEGER range

            // Get segment time range from data attributes
            const anchorEl = editingSpeaker.anchorEl;
            const deleteBtn = anchorEl?.querySelector('.speaker-delete-btn') as HTMLElement;
            const segmentStartSec = deleteBtn?.dataset.segmentStartSec ? parseFloat(deleteBtn.dataset.segmentStartSec) : null;
            const segmentEndSec = deleteBtn?.dataset.segmentEndSec ? parseFloat(deleteBtn.dataset.segmentEndSec) : null;

            // If it's a global override and we have segment time range, create a segment-specific override
            // This allows changing ONE instance while keeping others
            if (isGlobalOverride && segmentStartSec !== null && segmentEndSec !== null) {
                const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;
                // Use a NEW color for this new speaker (don't inherit from old)
                // Include both override colors AND diarization colors (from DEFAULT_SPEAKER_COLORS)
                const usedColors = new Set<string>(
                    ScriptLayerStore.speakerOverrides.map(o => o.color).filter(Boolean) as string[]
                );
                // Add current tag's color (diarization color)
                if (editingSpeaker.color) usedColors.add(editingSpeaker.color);
                // Add all DEFAULT_SPEAKER_COLORS that might be in use by diarization
                // (first 6 colors are commonly used for speaker_0 through speaker_5)
                DEFAULT_SPEAKER_COLORS.slice(0, 6).forEach(c => usedColors.add(c));
                const newColor = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));

                console.log('[V2LexicalTextEditor] Creating segment-specific override (changing from global):', {
                    speakerId,
                    diarizationSpeakerId,
                    sourceIndex: editingSpeaker.sourceIndex,
                    name: speakerName,
                    segmentStartSec,
                    segmentEndSec,
                    newColor,
                });
                ScriptLayerStore.addSpeakerOverride(
                    segmentStartSec,
                    segmentEndSec,
                    speakerName,
                    newColor,
                    [],
                    speakerId,
                    diarizationSpeakerId,
                    editingSpeaker.sourceIndex
                );
                setEditingSpeaker(null);
                CompositionStateStore.setUpdateTimestamp(Date.now());
                restoreScroll();
                return;
            }

            // For non-global overrides, update in place
            // Use a NEW color if this is a different speaker
            const existingOverrideWithSameSpeaker = ScriptLayerStore.speakerOverrides.find(
                o => o.speakerId === speakerId && o.id !== editingSpeaker.overrideId
            );
            let color = existingOverrideWithSameSpeaker?.color;
            if (!color && existingOverride?.speakerId !== speakerId) {
                // Different speaker - get a new color that avoids all used colors
                const usedColors = new Set<string>(
                    ScriptLayerStore.speakerOverrides.map(o => o.color).filter(Boolean) as string[]
                );
                if (editingSpeaker.color) usedColors.add(editingSpeaker.color);
                DEFAULT_SPEAKER_COLORS.slice(0, 6).forEach(c => usedColors.add(c));
                color = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));
            }

            ScriptLayerStore.updateSpeakerOverride(editingSpeaker.overrideId, {
                speakerName,
                speakerId,
                ...(color && { color })
            });
            setEditingSpeaker(null);
            CompositionStateStore.setUpdateTimestamp(Date.now());
            restoreScroll();
            return;
        }
        
        // Handle original speaker (not an override) - create override for ALL instances of this diarized speaker
        // Use full time range (0 to infinity) with diarizationSpeakerId to match all words from this speaker
        // editingSpeaker.speakerId is the diarization speaker ID (e.g., "speaker_0")
        const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;

        if (diarizationSpeakerId) {
            console.log('[V2LexicalTextEditor] Creating override for ALL instances of diarized speaker:', {
                speakerId,
                diarizationSpeakerId,
                sourceIndex: editingSpeaker.sourceIndex,
                name: speakerName,
            });
            // Get a color for this speaker (use existing or generate)
            const existingOverrideWithSameSpeaker = ScriptLayerStore.speakerOverrides.find(
                o => o.speakerId === speakerId
            );
            const color = existingOverrideWithSameSpeaker?.color || editingSpeaker.color;

            // Use 0 to MAX_VALUE to cover ALL words with this diarization speaker ID
            ScriptLayerStore.addSpeakerOverride(
                0,
                Number.MAX_SAFE_INTEGER,
                speakerName,
                color,
                [],
                speakerId,
                diarizationSpeakerId,
                editingSpeaker.sourceIndex
            );
            setEditingSpeaker(null);
            CompositionStateStore.setUpdateTimestamp(Date.now());
            restoreScroll();
            return;
        }
        
        setEditingSpeaker(null);
        restoreScroll();
    };

    const handleSpeakerEditSave = async () => {
        // Save scroll position before any state changes
        const scrollContainer = document.querySelector('.v2editor-scroll') as HTMLElement;
        const savedScrollTop = scrollContainer?.scrollTop ?? 0;
        
        const restoreScroll = () => {
            requestAnimationFrame(() => {
                if (scrollContainer) {
                    scrollContainer.scrollTop = savedScrollTop;
                    requestAnimationFrame(() => {
                        if (scrollContainer) {
                            scrollContainer.scrollTop = savedScrollTop;
                        }
                    });
                }
            });
        };
        
        if (!editingSpeaker) {
            ScriptLayerStore.setPendingSpeakerOverride(null);
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }

        const newName = editValue.trim();
        
        // If editing was cancelled with empty name for a pending override
        if (!newName && editingSpeaker.isPending) {
            ScriptLayerStore.setPendingSpeakerOverride(null);
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }
        
        if (!newName) {
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }

        // Check if this speaker name already exists in the project (Firestore or local overrides)
        const existingFirestoreSpeaker = ProjectAssetsStore.projectSpeakers.find(
            (s: any) => s.name.toLowerCase() === newName.toLowerCase()
        );
        const existingOverrideSpeaker = ScriptLayerStore.speakerOverrides.find(
            o => o.speakerName.toLowerCase() === newName.toLowerCase() && o.speakerId
        );
        
        let speakerId: string | undefined;
        
        if (existingFirestoreSpeaker) {
            // Use existing Firestore speaker
            speakerId = existingFirestoreSpeaker.speakerId || existingFirestoreSpeaker.id;
            console.log('[V2LexicalTextEditor] Using existing Firestore speaker:', speakerId, newName);
        } else if (existingOverrideSpeaker?.speakerId) {
            // Use speaker ID from existing local override
            speakerId = existingOverrideSpeaker.speakerId;
            console.log('[V2LexicalTextEditor] Using existing override speaker:', speakerId, newName);
        } else {
            // Create new speaker in Firestore
            const projectId = ProjectAssetsStore.projectId;
            if (projectId) {
                try {
                    const newSpeakerId = await CreateSpeaker({ projectId, name: newName });
                    speakerId = newSpeakerId;
                    console.log('[V2LexicalTextEditor] Created new speaker:', speakerId, newName);
                } catch (error) {
                    console.error('[V2LexicalTextEditor] Failed to create speaker:', error);
                }
            }
        }

        // Handle pending speaker override - create new override
        if (editingSpeaker.isPending && ScriptLayerStore.pendingSpeakerOverride) {
            const { startSec, endSec } = ScriptLayerStore.pendingSpeakerOverride;

            // Use the same color as other overrides with this speaker (for consistency/merging)
            const existingOverrideWithSameSpeaker = speakerId
                ? ScriptLayerStore.speakerOverrides.find(o => o.speakerId === speakerId)
                : null;
            const color = existingOverrideWithSameSpeaker?.color || editingSpeaker.color;

            // editingSpeaker.speakerId is the diarization speaker ID (e.g., "speaker_0")
            const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;
            console.log('[V2LexicalTextEditor] Saving pending speaker override:', {
                name: newName,
                color,
                speakerId,
                diarizationSpeakerId,
                sourceIndex: editingSpeaker.sourceIndex,
                startSec,
                endSec,
            });
            ScriptLayerStore.addSpeakerOverride(
                startSec,
                endSec,
                newName,
                color,
                [],
                speakerId,
                diarizationSpeakerId,
                editingSpeaker.sourceIndex
            );
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }
        
        // Handle existing override - check if it's a "global" override
        if (editingSpeaker.overrideId) {
            const existingOverride = ScriptLayerStore.speakerOverrides.find(o => o.id === editingSpeaker.overrideId);
            const isGlobalOverride = existingOverride && existingOverride.endSec > 1000000000;

            // Get segment time range from data attributes
            const anchorEl = editingSpeaker.anchorEl;
            const deleteBtn = anchorEl?.querySelector('.speaker-delete-btn') as HTMLElement;
            const segmentStartSec = deleteBtn?.dataset.segmentStartSec ? parseFloat(deleteBtn.dataset.segmentStartSec) : null;
            const segmentEndSec = deleteBtn?.dataset.segmentEndSec ? parseFloat(deleteBtn.dataset.segmentEndSec) : null;

            // If it's a global override and we have segment time range, create a segment-specific override
            if (isGlobalOverride && segmentStartSec !== null && segmentEndSec !== null) {
                const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;
                // Use a NEW color for this new speaker (avoid all used colors including diarization)
                const usedColors = new Set<string>(
                    ScriptLayerStore.speakerOverrides.map(o => o.color).filter(Boolean) as string[]
                );
                if (editingSpeaker.color) usedColors.add(editingSpeaker.color);
                DEFAULT_SPEAKER_COLORS.slice(0, 6).forEach(c => usedColors.add(c));
                const newColor = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));

                console.log('[V2LexicalTextEditor] Creating segment-specific override (edit save, from global):', {
                    speakerId,
                    diarizationSpeakerId,
                    sourceIndex: editingSpeaker.sourceIndex,
                    name: newName,
                    segmentStartSec,
                    segmentEndSec,
                    newColor,
                });
                ScriptLayerStore.addSpeakerOverride(
                    segmentStartSec,
                    segmentEndSec,
                    newName,
                    newColor,
                    [],
                    speakerId,
                    diarizationSpeakerId,
                    editingSpeaker.sourceIndex
                );
                setEditingSpeaker(null);
                CompositionStateStore.setUpdateTimestamp(Date.now());
                restoreScroll();
                return;
            }

            // For non-global overrides, update in place
            // Use a NEW color if this is a different speaker
            let color: string | undefined;
            if (existingOverride?.speakerId !== speakerId) {
                const usedColors = new Set<string>(
                    ScriptLayerStore.speakerOverrides.map(o => o.color).filter(Boolean) as string[]
                );
                if (editingSpeaker.color) usedColors.add(editingSpeaker.color);
                DEFAULT_SPEAKER_COLORS.slice(0, 6).forEach(c => usedColors.add(c));
                color = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));
            }

            ScriptLayerStore.updateSpeakerOverride(editingSpeaker.overrideId, {
                speakerName: newName,
                speakerId,
                ...(color && { color })
            });
            setEditingSpeaker(null);
            CompositionStateStore.setUpdateTimestamp(Date.now());
            restoreScroll();
            return;
        }

        if (newName === editingSpeaker.displayName) {
            setEditingSpeaker(null);
            restoreScroll();
            return;
        }

        // Handle original speaker (not an override) - create override for ALL instances of this diarized speaker
        // editingSpeaker.speakerId is the diarization speaker ID (e.g., "speaker_0")
        const diarizationSpeakerId = editingSpeaker.speakerId != null ? String(editingSpeaker.speakerId) : undefined;

        if (diarizationSpeakerId) {
            // Create override for all instances of this diarized speaker
            console.log('[V2LexicalTextEditor] Creating override for ALL instances of diarized speaker (edit save):', {
                speakerId,
                diarizationSpeakerId,
                sourceIndex: editingSpeaker.sourceIndex,
                name: newName,
            });
            const existingOverrideWithSameSpeaker = speakerId
                ? ScriptLayerStore.speakerOverrides.find(o => o.speakerId === speakerId)
                : null;
            const color = existingOverrideWithSameSpeaker?.color || editingSpeaker.color;

            // Use 0 to MAX_VALUE to cover ALL words with this diarization speaker ID
            ScriptLayerStore.addSpeakerOverride(
                0,
                Number.MAX_SAFE_INTEGER,
                newName,
                color,
                [],
                speakerId,
                diarizationSpeakerId,
                editingSpeaker.sourceIndex
            );
            setEditingSpeaker(null);
            CompositionStateStore.setUpdateTimestamp(Date.now());
            restoreScroll();
            return;
        }

        // Fallback: Update the ScriptLayerClip's names array (for non-diarized sources)
        const clip = CompositionService.getClipById(editingSpeaker.clipId);
        if (clip && isScriptClip(clip)) {
            const scriptClip = clip as ScriptLayerClip;
            if (scriptClip.names[editingSpeaker.sourceIndex] !== undefined) {
                scriptClip.names[editingSpeaker.sourceIndex] = newName;
            }
        }

        setEditingSpeaker(null);
        
        // Trigger a re-render of the transcript to pick up the new name
        CompositionStateStore.setUpdateTimestamp(Date.now());
        restoreScroll();
    };

    const handleSpeakerEditKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSpeakerEditSave();
        } else if (e.key === 'Escape') {
            // Cancel editing
            if (editingSpeaker?.isPending) {
                ScriptLayerStore.setPendingSpeakerOverride(null);
            }
            setEditingSpeaker(null);
        }
    };

    return (
	        <LexicalComposer initialConfig={initialConfig}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    width: "100%",
                    height: "100%",
                    minHeight: 0,
                }}
            >
	                <GlobalStyles styles={{
	                    // Scrollbar styling
	                    '.v2editor-scroll::-webkit-scrollbar': { width: 6 },
	                    '.v2editor-scroll::-webkit-scrollbar-track': { background: 'transparent' },
	                    '.v2editor-scroll::-webkit-scrollbar-thumb': { background: 'var(--app-text-subtle)', borderRadius: 3 },
	                    '.v2editor-scroll::-webkit-scrollbar-thumb:hover': { background: 'var(--app-text-muted)' },
	                    '.v2editor-scroll': { scrollbarWidth: 'thin', scrollbarColor: 'var(--app-text-subtle) transparent' },
	                    // Paragraph styling
	                    '.v2lexical-p': {
	                        margin: '0 0 1.35em 0',
	                        padding: 0,
	                    },
                        // Speaker label paragraphs should sit tighter to the spoken paragraph below.
                        '.v2lexical-p:has(.speaker-label)': {
                            margin: '0 0 0.45em 0',
                        },
	                    // Word hover effect
	                    '.v2editor-scroll span[data-lexical-text="true"]:hover': {
	                        backgroundColor: 'rgba(59, 130, 246, 0.08)',
	                        borderRadius: '2px',
                        cursor: 'pointer',
                    },
                    // Selection styling
                    '.v2editor-scroll ::selection': {
                        backgroundColor: 'rgba(59, 130, 246, 0.2)',
                    },
                    // Playback word highlight
                    '.word--highlighted': {
                        backgroundColor: 'rgba(43, 107, 253, 1.0)',
                        borderRadius: '3px',
                        padding: '2px 4px',
                        margin: '-2px -4px',
                        transition: 'background-color 0.1s ease',
                        color: '#FFFFFF',
                    },
                }} />
	                <Box
	                    ref={editorOverlayRef}
	                    className="v2editor-transcript-root"
                        data-tour="transcript-panel"
	                    sx={{ position: 'relative', flex: 1, minHeight: 0 }}
	                >
	                    <RichTextPlugin
	                        contentEditable={<ContentEditable className="v2editor-scroll" style={editorInputStyle}/>}
	                        placeholder={null}
	                        ErrorBoundary={undefined}
	                    />
	                    <TranscribingPlaceholder />
	                    <Toolbar 
	                        containerRef={editorOverlayRef as React.RefObject<HTMLElement>}
	                        onChangeSpeaker={(startSec, endSec) => {
	                            // Set pending speaker override - this will trigger the UI to show an editable label
	                            ScriptLayerStore.setPendingSpeakerOverride({ startSec, endSec });
	                        }}
	                        onDuplicateToNewComposition={onDuplicateToNewComposition}
	                    />
                        <EmptyLineActionsOverlay containerRef={editorOverlayRef as React.RefObject<HTMLElement>} />
	                    {/* Marker rename box (kept inside the editor container, like "Correct word") */}
	                    {editingMarker ? (
	                        <Box
	                            sx={{
	                                position: "absolute",
	                                top: `${Math.max(8, editingMarker.anchorPos.top - 52)}px`,
	                                left: `${Math.max(8, editingMarker.anchorPos.left)}px`,
	                                backgroundColor: "var(--app-bg-surface)",
	                                border: "1px solid var(--app-border-subtle)",
	                                borderRadius: "10px",
	                                boxShadow: "0 18px 40px rgba(0,0,0,0.16)",
	                                padding: "8px",
	                                display: "flex",
	                                alignItems: "center",
	                                gap: 1,
	                                zIndex: 1002,
	                                maxWidth: "min(520px, calc(100% - 16px))",
	                            }}
	                            onMouseDown={(e) => {
	                                e.stopPropagation();
	                            }}
	                        >
	                            <TextField
	                                value={markerEditValue}
	                                onChange={(e) => setMarkerEditValue(e.target.value)}
	                                inputRef={markerEditInputRef}
	                                size="small"
	                                autoFocus
	                                onKeyDown={(e) => {
	                                    e.stopPropagation();
	                                    if (e.key === "Enter") {
	                                        e.preventDefault();
	                                        applyMarkerRename();
	                                    } else if (e.key === "Escape") {
	                                        e.preventDefault();
	                                        setEditingMarker(null);
	                                    }
	                                }}
	                                placeholder="Marker"
	                                sx={{
	                                    flex: 1,
	                                    minWidth: 160,
	                                    "& .MuiInputBase-input": {
	                                        fontFamily: "Inter, sans-serif",
	                                        fontSize: "13px",
	                                    },
	                                }}
	                            />
	                            <IconButton
	                                size="small"
	                                onMouseDown={(e) => {
	                                    e.preventDefault();
	                                    e.stopPropagation();
	                                }}
	                                onClick={() => setEditingMarker(null)}
	                            >
	                                <CloseIcon fontSize="small" />
	                            </IconButton>
	                        </Box>
	                    ) : null}
	                </Box>
                <LoadWordsPlugin words={words}/>
                <RangeSyncPlugin/>
                <PlaybackHighlightPlugin/>
                <SceneSplitPlugin/>
                <MarkerInsertPlugin/>
                <ScriptChunkClipboardPlugin/>
                <ParagraphBreakPlugin/>
	                <BackspaceParagraphMergePlugin/>
	                {readOnly ? <RestrictedTranscriptInputPlugin/> : null}
	                <NodeEventPlugin nodeType={WordNode} eventType={'click'} eventListener={handleWordNodeClick}/>
	                <NodeEventPlugin nodeType={SceneBreakNode} eventType={'click'} eventListener={handleSceneBreakNodeClick}/>
	                <NodeEventPlugin nodeType={SceneBreakNode} eventType={'contextmenu'} eventListener={handleSceneBreakNodeContextMenu}/>
	                <NodeEventPlugin nodeType={MarkerNode} eventType={'click'} eventListener={handleMarkerNodeClick}/>
	                <NodeEventPlugin nodeType={MarkerNode} eventType={'dblclick'} eventListener={handleMarkerNodeDoubleClick}/>
	                <NodeEventPlugin nodeType={SpeakerNode} eventType={'click'} eventListener={handleSpeakerNodeClick}/>
                    <NodeEventPlugin nodeType={SpeakerNode} eventType={'contextmenu'} eventListener={handleSpeakerNodeContextMenu}/>
	                <HistoryPlugin/>
                <OnChangePlugin onChange={onChange}/>
                <TranscriptExportRegistrationPlugin onReady={onTranscriptExporterReady} />
            </Box>

	            {/* Speaker name edit popover with speaker picker */}
	            <Popover
                open={!!editingSpeaker}
                anchorEl={editingSpeaker?.anchorEl}
                onClose={(event, reason) => {
                    // Don't close on backdrop click for pending speakers - only on explicit action
                    if (editingSpeaker?.isPending && reason === 'backdropClick') {
                        // If empty, cancel the pending override
                        if (!editValue.trim()) {
                            ScriptLayerStore.setPendingSpeakerOverride(null);
                        }
                    }
                    setEditingSpeaker(null);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                disableAutoFocus={false}
                disableEnforceFocus={false}
                disableScrollLock={true}
                disablePortal={false}
                PaperProps={{
                    sx: {
                        p: 0,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        mt: 0.5,
                        minWidth: '220px',
                        overflow: 'hidden',
                    }
                }}
            >
                <Box sx={{ p: 1.5 }}>
                <TextField
                    inputRef={editInputRef}
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleSpeakerEditKeyDown}
                    size="small"
                    variant="outlined"
                        placeholder={editingSpeaker?.isPending ? "Enter speaker name" : "Speaker name"}
                        autoFocus
                        fullWidth
                        InputProps={{
                            endAdornment: editValue.trim() && !ProjectAssetsStore.projectSpeakers.some((s: any) => s.name.toLowerCase() === editValue.trim().toLowerCase()) ? (
                                <InputAdornment position="end">
                                    <Typography variant="caption" sx={{ color: 'var(--app-text-subtle)', fontSize: '11px' }}>
                                        ↵ to create
                                    </Typography>
                                </InputAdornment>
                            ) : null,
                        }}
                    sx={{
                        '& .MuiInputBase-input': {
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: editingSpeaker?.color || 'var(--app-text-primary)',
                        },
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: editingSpeaker?.color || 'var(--app-border-subtle)',
                            },
                            '&:hover fieldset': {
                                borderColor: editingSpeaker?.color || 'var(--app-border-muted)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: editingSpeaker?.color || '#2B6BFD',
                            },
                        },
                    }}
                />
                </Box>
                
                {/* Existing speakers in project */}
                {ProjectAssetsStore.projectSpeakers.length > 0 && (
                    <>
                        <Divider />
                        <Box sx={{ px: 1.5, py: 0.75 }}>
                            <Typography variant="caption" sx={{ color: 'var(--app-text-subtle)', fontSize: '11px', fontWeight: 500 }}>
                                In this project
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0, maxHeight: '200px', overflowY: 'auto' }}>
                            {ProjectAssetsStore.projectSpeakers
                                .filter((speaker: any) => {
                                    // Show all if empty, or filter by input
                                    if (!editValue.trim()) return true;
                                    return speaker.name.toLowerCase().includes(editValue.trim().toLowerCase());
                                })
                                .map((speaker: any) => (
                                    <ListItemButton
                                        key={speaker.speakerId || speaker.id}
                                        onClick={() => {
                                            // Select existing speaker
                                            handleSelectExistingSpeaker(speaker);
                                        }}
                                        sx={{
                                            py: 0.75,
                                            px: 1.5,
                                            '&:hover': {
                                                backgroundColor: 'var(--app-bg-subtle)',
                                            },
                                        }}
                                    >
                                        <ListItemText 
                                            primary={speaker.name}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontFamily: 'Inter',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    color: speaker.name.toLowerCase() === editValue.trim().toLowerCase() 
                                                        ? editingSpeaker?.color || '#3B82F6' 
                                                        : 'var(--app-text-primary)',
                                                }
                                            }}
                                        />
                                    </ListItemButton>
                                ))
                            }
                        </List>
                    </>
                )}
            </Popover>
        </LexicalComposer>
    );
});

export default V2LexicalTextEditor;
